/**
 * WORLD CONTENT — trailheads, run identity, results, and making the mountain legible.
 *
 * OWNER: piece:world-content
 *
 * ---------------------------------------------------------------------------
 * THE DEFECT THIS FILE EXISTS FOR
 *
 * The wave-5 coherence pass rode the whole game and found an entire content system the
 * player cannot see. Measured on this build (`captures/w5-content-tools/net-dump.mjs`,
 * which reads `__gripTrails.network()` out of the live page):
 *
 *   20 named runs, 44 B-lines, 276 features, 16.5 km of built line
 *   3 trailheads   Summit Gate (758 m, 14 runs), Upper Saddle (899 m, 4), Ridge Station (895 m, 2)
 *   Sunday Best 1765 m / 264 m drop / blue / flow      Widowmaker 1047 m / 265 m / black / tech
 *   Dustbowl, Sandblaster, Loose Change, Blue Steel, Kitty Litter, Ripsaw, Sidewinder,
 *   Crossover, The Connector, Traverse 2..9 ...
 *
 * The world spawn sits at 516.8 m along Sunday Best, on its tread, and until this file
 * shipped nothing anywhere in the product said so. Loose Change branches off 14 m from
 * the spawn and there was no way to know it was there, what it was, or where it went.
 *
 * ---------------------------------------------------------------------------
 * WHAT SHIPS, AND WHERE EACH PART LIVES
 *
 *   1. SIGNAGE IN THE WORLD  (this file, three.js)
 *      Trailhead kiosks with a real drawn map of the network; a marker at the head of
 *      every named run; a fingerpost at every junction where two named runs part; a small
 *      blaze at every B-line split. One mesh, one texture, one draw call.
 *
 *   2. RUN IDENTITY WHILE RIDING  (`src/ui/hud.ts`, driven from here)
 *      Name, grade, character, length, progress, distance to go. Top-left, four short
 *      lines, identity palette, and its painted area is an audited budget.
 *
 *   3. A RESULT AT THE BOTTOM  (`src/ui/hud.ts`, driven from here)
 *      Time, top and average speed, air, features cleared clean, crashes, and the delta
 *      against your best descent of that run.
 *
 *   4. LEGIBILITY FROM THE SADDLE  (this file + hud)
 *      Up to four world-space labels, terrain-occlusion tested, that appear when they are
 *      useful — approaching a junction where the line splits, rolling slowly, off the
 *      tread, or in the first seconds of a session — and get out of the way at speed.
 *
 * ---------------------------------------------------------------------------
 * ROUND 2 — THE AUTHORED SIGNAGE NOW SURVIVES THE RENDER PATH
 *
 * R1 built all of the above and the critique found that the in-world half was invisible:
 * the atlas plate is L 212 and warm, and the same blade on the glass measured L 44-63,
 * cold blue, BELOW the dirt it stands in. 85 objects read as charred stakes and dark
 * slabs, the HUD chips did all the identifying, and half the piece was for nothing.
 *
 * Measured with `captures/w5-content-tools/plate-probe.mjs`, which reads the plate's own
 * pixels off the live canvas (see THE INSTRUMENT below), 10 plates, one boot, the same
 * vantages at both settings:
 *
 *                       plate L   worst   ground L   gap     plate RGB
 *     R1 (shipped)       63.1     47.9     141.6     -78.5   (56,64,77)   cold blue
 *     R2 (this file)    154.8    146.3     141.5     +13.3   (158,154,155)
 *
 * Three changes, and each is documented at its constant rather than here:
 *   PLATE_GAIN   the plate is retroreflective sheeting, not a Lambertian card
 *   C_TIMBER     weathered cedar goes SILVER, not dark brown — the "charred stakes"
 *   POI_SIGN_LEGIBLE_PX  once the object is legible, its HUD chip stands down
 *
 * The cause was NOT any of the four things that looked likely — not terrain shadow, not
 * the rider's own shadow, not aerial perspective, not fog. The probe switched each of
 * them off in turn and the plate did not move by 6 L. See PLATE_GAIN.
 *
 * ---------------------------------------------------------------------------
 * THE SIGNAGE VOCABULARY IS BORROWED, NOT INVENTED
 *
 * International MTB signage is a real, learned grammar and a rider reads it before they
 * read a word: green circle / blue square / black diamond / double black diamond, always
 * on a pale plate, on a post at the head of the run and again at every junction. Using it
 * means the HUD blaze and the sign blaze are the same four symbols, so the strip in the
 * corner and the board on the post teach each other.
 *
 * `docs/BAR.md` governs how the objects sit in the world — "simplified but correctly
 * proportioned", "contact where things meet ground". So: a 100 mm square post, boards at
 * 620 x 155 mm, marker heads at 1.42 m, fingerposts at 1.78 m, a trailhead map board of
 * 1.15 x 0.85 m at 22 degrees off vertical on two 1.9 m legs. Those are the dimensions of
 * the real objects. The board *art* is flat and graphic, which is not a violation of the
 * stylized-grounded direction — a trail sign is a painted plate, and painting it flat is
 * what makes it physically truthful.
 *
 * `docs/IDENTITY.md` governs the art on the plates: ink ground, bone type, one accent,
 * hard edges, no gradients, no glow.
 *
 * ---------------------------------------------------------------------------
 * THE INSTRUMENT, AND HOW IT WAS SHOWN TO FAIL
 *
 * Five waves, five measurement bugs; two of them were metrics structurally incapable of
 * seeing the thing they were watching. So the claim "the player can now see the content"
 * is measured two ways, neither of which is this file's own bookkeeping, and both were
 * broken on purpose before either was believed.
 *
 *   a) `content.runNamedOnScreen` reads the run name back out of the HUD's *text node*
 *      and compares it against `net.probe()` — the trail network's own answer to "what is
 *      under the bike". Two independent sources; the invariant is the disagreement.
 *
 *      FALSIFIED, `captures/w5-content-tools/visibility-probe.mjs`:
 *        shipped build, parked on Sunday Best     0 violations / 23 samples
 *        run strip `display:none`                23 violations / 23
 *        run strip pushed off the left edge      23 violations / 23
 *        restored                                 0 violations / 23
 *
 *      The first draft of `hudChromeMetrics()` read only the inline opacity, and the
 *      `display:none` case PASSED SILENTLY — the text node was still full, so the metric
 *      reported the name as visible while the screen showed nothing. That is the exact
 *      shape of the two structurally-blind metrics wave 4 shipped. It reads a real
 *      `getBoundingClientRect` and an on-screen test now, and that is why.
 *
 *      On the gate scenario: **0 violations in 511 samples over 1,209 m of `fullrun`.**
 *
 *   c) R2 added a third, `captures/w5-content-tools/plate-probe.mjs`, because "the sign
 *      is on screen" and "the sign READS" are different claims and only the first had an
 *      instrument. It identifies a plate's own pixels two independent ways at once —
 *      the four world corners of the painted face as the GPU draws them
 *      (`SignGeometry.lastFront`, not a reconstruction from constants), AND an A/B on the
 *      sign group — and reports the plate's value against the ground beneath it.
 *
 *      FOUR OF ITS OWN BUGS WERE FOUND AND KILLED BEFORE ANY NUMBER WAS BELIEVED, and
 *      every one of them produced a plausible-looking result first:
 *        - a hand-rolled projection collapsed the horizontal extent to 0.04 px and filed
 *          every marker as "too few plate pixels". Replaced with THREE's own
 *          `Vector3.project`. Reimplementing the engine's arithmetic to check the engine
 *          is how you measure your own algebra instead of the game.
 *        - the vantage search accepted the first stable camera it found, which for a
 *          marker was 680 m away. It now scores vantages by projected plate area and
 *          rejects any where the plate is not on the glass.
 *        - `stepFrames(1, 0)` is NOT a no-op — ChaseCamera clamps dt up to 1/480 — so the
 *          A/B diff counted camera jitter as sign. The null control read L 185.7 on a
 *          build with the signs SWITCHED OFF. Fixed with a third frame one redraw from A
 *          as a jitter reference; the null is now 0-4 px in ~24,000, stated as a ratio.
 *        - the first positive control set `material.emissive` directly, which
 *          `updatePlateGain()` overwrites every frame, so the control was undone before
 *          the frame it was meant to change and reported 80.6 against a base of 80.3.
 *          Both controls now drive the same authored input the game drives.
 *
 *      Controls, current build: null 0 px of 24,458 (0.00); gain x20 146.1 -> 208.9;
 *      albedo black + gain 0 146.1 -> 9.7.
 *
 *   b) The 3D signage is proved by an interleaved A/B pixel diff in the real renderer,
 *      with the null measured at the same vantage, in the same frame, over the same span
 *      of draws. Four earlier versions of that instrument were wrong and each looked like
 *      a result — the first reported 498,877 "sign pixels" on a build with the signs
 *      switched off. The probe's header records all four and what killed them.
 *
 *      The clincher is the negative control: at the fingerpost 7 m up Sunday Best, with
 *      NOTHING changed except the sign group teleported 5 km underground, the verdict
 *      flips from **2,732x its own null (314,218 px on the glass) to 0.4x (43 px,
 *      inconclusive)**.
 *
 *      Four of eight vantages resolve ON GLASS at 17x-2,732x; the other four are
 *      INCONCLUSIVE — the sign is small or occluded from where the rider was put — which
 *      is not the same as absent and is not reported as such.
 *
 * ---------------------------------------------------------------------------
 * PERFORMANCE
 *
 * Build: one 2048x2048 canvas, one merged BufferGeometry, one MeshStandardMaterial. 85
 * signs = 189 boxes = 2,268 triangles in ONE draw call.
 *
 * Run: one `net.probe()`, a candidate refresh at ~7 Hz, and up to four projections with a
 * 10-step terrain occlusion march each. Measured `content.frameMs` worst 0.20 ms against
 * a 0.35 budget, mean 0.11, over 1,143 samples; the instrument's own forced layout is a
 * separate number (`content.auditMs`, 0.10 ms worst against 1.2) so the two can never be
 * confused — folding them together is what put R1 over budget and looked like a cost the
 * gameplay path did not have.
 */

import * as THREE from 'three';
import { registerSystem, type SystemContext } from '../core/systems';
import {
  getTrailNetwork,
  type TrailNetworkApi,
  type TrailRunInfo,
  type TrailheadInfo,
  type TrailCentrelinePoint,
} from '../world/trails';
import type { Terrain } from '../world/terrain';
import { Identity, srgbHex, oklch } from '../render/palette';
import { declareInvariant } from '../core/audit';
import {
  runStrip, poiLabels, MAX_POI, TREAD_NEAR_M, showRunResult, hideRunResult, runResultVisible,
  hudChromeMetrics, type RunResultCard, type BlazeGrade,
} from '../ui/hud';

// ===========================================================================
// 1. CONSTANTS — the physical dimensions of the objects, and the display rules
// ===========================================================================

/** Post section, m. A 4x4 in the ground, which is what a trail marker post is. */
const POST_W = 0.11;
/** Height of a run-head marker post above ground, m. */
const MARKER_H = 1.58;
/** Height of a junction fingerpost, m. Taller: it is read from further away. */
const FINGER_H = 1.92;
/** Height of a B-line blaze post, m. Knee-to-waist, read at the moment you pass it. */
const BLINE_H = 1.02;
/** Legs of a trailhead kiosk, m. */
const KIOSK_H = 1.90;
/**
 * How far the post is buried, m. Nothing sits ON the ground; posts go IN it.
 *
 * 0.30 rather than a token 0.05 because signs go on the cut bank, and the trail's own
 * batter is 1.7:1 (`src/world/trails.ts CUT_SLOPE`). Across a 0.10 m post on a 1.7 slope
 * the ground moves 0.17 m corner to corner, so a post seated at the centre height alone
 * would visibly float on its low side — "no contact between objects and ground" is one of
 * the two named failure modes in `docs/BAR.md`. The base is seated at the LOWEST of five
 * samples across the footprint and then sunk this far below it.
 */
const POST_SINK = 0.30;

/**
 * Name board, m. 800 x 210 x 24 mm — a real routed blade sign, at the big end of the
 * range rather than the small one.
 *
 * R1 shipped 620 x 155, which is a correct dimension for a trail blade and was the wrong
 * choice: measured off `captures/w5-content-gallery/finger_Dustbowl_near.png`, a 155 mm
 * board at riding distance is eleven pixels tall and the name is a smudge. The rule in
 * `docs/BAR.md` is "simplified but correctly proportioned", and 800 x 210 is still a real
 * signpost — it is the size a junction fingerpost blade actually is — while being legible
 * from the saddle, which is the only reason the object exists.
 */
const BOARD_W = 0.80;
const BOARD_H = 0.21;
const BOARD_T = 0.024;
/** Small blaze plate on a B-line post, m. */
const BLAZE_W = 0.24;
const BLAZE_H = 0.24;
/** Trailhead map board, m, and how far it leans back from vertical. */
const MAP_W = 1.30;
const MAP_H = 0.95;
const MAP_TILT = 0.38; // rad, ~22 degrees

/**
 * How far off the centreline a post is planted, past the tread edge, m.
 *
 * Trail-building practice is that furniture goes on the CUT side — the uphill one — where
 * the ground is solid and where a rider who loses the line is not already falling. The
 * side is chosen per sign by sampling the terrain either side, not assumed.
 */
const SIGN_OFFSET = 1.35;

/**
 * Minimum spacing between two signs, m.
 *
 * The network hands us 16 named-run junctions and 44 B-line splits, and several of them
 * are within a couple of metres of each other — jct-0 and jct-58 are both 14 m from the
 * spawn. Two posts a metre apart is not signage, it is litter, and it is the difference
 * between a built mountain and a prototype. Enforced at build, audited as
 * `content.signSpacing`.
 */
const SIGN_MIN_SPACING = 9;

/** Speed below which the world labels come up, m/s. Above it the screen stays clear. */
const POI_SLOW = 7;
/** Seconds after a session starts during which the labels are up regardless. */
const POI_ORIENT_S = 9;
/** How far ahead a junction starts advertising itself, m. */
const POI_JUNCTION_RANGE = 55;
/** Furthest a trailhead or run-head label is drawn, m. */
const POI_FAR = 620;
/** Distance over which a label fades in as it comes inside `POI_FAR`, m. */
const POI_FADE = 140;

/**
 * Screen height, px, at which a sign's own plate is readable and its HUD chip stands down.
 *
 * R2 fixed the sign material, and the moment the in-world half started working the two
 * halves began saying the same thing twice: `captures/w5-content-gallery/
 * kiosk_SummitGate_near.png` has a chip reading SUMMIT GATE floating directly above a
 * kiosk whose own header reads SUMMIT GATE. `docs/BAR.md` asks for a clear screen, and a
 * label that repeats an object the player is looking at is the least defensible pixel on
 * it.
 *
 * 30 px is derived, not picked. The name occupies 46 of the 128 px of a board in the
 * atlas — 36% of the plate's height — so a plate 30 px tall puts an 11 px cap height on
 * the glass, which is where the name stops being a smudge.
 *
 * What that works out to is worth stating plainly rather than hiding, because it is the
 * honest answer to the other half of this round's brief:
 *
 *     kiosk map board  0.95 m  ->  chip stands down inside 23 m
 *     name blade       0.21 m  ->  chip stands down inside  5 m
 *
 * A 0.21 m blade IS a real trail blade and it is not readable past about five metres in
 * life either. That is precisely why the chips exist and why they are not simply deleted
 * now that the signs work: the object identifies itself when you are on top of it, the
 * chip identifies it from across the valley, and neither does both.
 */
const POI_SIGN_LEGIBLE_PX = 30;

/** How close to the end of a run counts as finishing it, m. */
const RUN_END_TOL = 14;
/** A result only fires if this much of the run was actually ridden, as a fraction. */
const RUN_MIN_FRACTION = 0.18;
/** ...or this many metres, whichever is larger. */
const RUN_MIN_METRES = 90;
/** Seconds a new run must be held before the strip switches to it. Junction hysteresis. */
const RUN_SWITCH_S = 0.35;
/** Seconds off any tread before the strip lets go of the run. */
const RUN_DROP_S = 2.2;

/**
 * How far the locator will look for a run to point at, m.
 *
 * Past this the answer stops being a way back and starts being a fact about the map, and
 * an arrow pointing at something a kilometre and a half away over two ridges is worse than
 * no arrow. 900 m is the whole width of the network's spread from any point inside it —
 * measured, `captures/w5-content-offrun/OFFRUN.json`: the furthest the `vista` rider ever
 * gets from a named centreline is 26.3 m, and the fullrun autopilot's worst ejection puts
 * it under 40. The limit exists for the case where somebody rides off the mountain, and
 * there it correctly says nothing rather than lying.
 */
const LOCATOR_FAR_M = 900;

/**
 * How often the nearest-run search runs, s.
 *
 * The SEARCH is what costs; the distance and bearing to the point it found are recomputed
 * every frame from the stored point, so the arrow and the metre count are smooth at 60 fps
 * while the scan happens eight times a second. At the fastest the bike goes (63.8 km/h on
 * `vista`) the found point is at most 2.2 m stale, which is under the 3.4 m spacing of the
 * centreline nodes the search picks from — i.e. inside its own resolution.
 */
const LOCATOR_REFRESH_S = 0.125;

// ===========================================================================
// 2. SELF-AUDIT — src/core/audit.ts
//
// Declared at module scope, handles kept, never looked up per frame.
// ===========================================================================

const SIGN_CLEARANCE = declareInvariant({
  id: 'content.signTreadClearance',
  owner: 'world-content',
  what: 'lateral gap from a planted signpost to the nearest built tread edge',
  unit: 'm',
  budget: 0.60,
  compare: 'min',
  phase: 'build',
  source: 'src/game/content.ts SIGN_OFFSET',
  consequence: 'a rider on the racing line hits a signpost',
});

const SIGN_SPACING = declareInvariant({
  id: 'content.signSpacing',
  owner: 'world-content',
  what: 'distance from a planted sign to its nearest neighbour',
  unit: 'm',
  budget: SIGN_MIN_SPACING,
  compare: 'min',
  phase: 'build',
  source: 'src/game/content.ts SIGN_MIN_SPACING',
  consequence: 'posts bunch into litter at junctions instead of reading as signage',
});

/**
 * Budget 1.40 and the number is chosen against `src/world/trails.ts CUT_SLOPE`, which is
 * 1.7. Under 1.4 means the post found ground OFF the trail's own batter rather than
 * standing in the cut face. It is a `warn` because a post on a bank is ugly, not unsafe,
 * and because the mountain's own max grade is 54% — some sites genuinely have nowhere
 * flatter and the honest thing is to say so rather than to leave the junction unsigned.
 */
const SIGN_SLOPE = declareInvariant({
  id: 'content.signGroundSlope',
  owner: 'world-content',
  what: 'ground gradient across the footprint a signpost is planted in',
  unit: 'm/m',
  budget: 1.40,
  compare: 'max',
  severity: 'warn',
  phase: 'build',
  source: 'src/game/content.ts plantSign vs trails CUT_SLOPE 1.7',
  consequence: 'a post stands in the cut face instead of on ground a rider could stand on',
});

/**
 * THE DEFECT AS A NUMBER.
 *
 * 1 on a sampled frame where the bike is on a named run's tread and the HUD is not
 * showing that run's name; 0 otherwise. `pctOver` is therefore the percentage of ridden
 * frames in which the player could not have learned what they were on — which was 100%
 * before this file existed, and is the whole reason it does.
 *
 * The two sides come from different places on purpose: the truth is `net.probe()` (the
 * trail network's own spatial query) and the claim is the HUD's text node read back
 * through a real bounding-rect measurement. Neither is this module's own bookkeeping.
 */
/**
 * THE CEILING ON THE FIX, AND THE AUDIT BUS EARNED ITS KEEP ON THIS ONE.
 *
 * The gain is a reflectance, not a light. Past its budget the plates are self-luminous: a
 * sun-facing blade goes over the ACES shoulder and clips, the lit and the shaded plate
 * converge on one flat white, and the blade stops having a front and a side.
 *
 * The first version of this budget was 0.35 and it was ARRIVED AT BY ARITHMETIC ON PAPER,
 * not by measurement. It shipped one capture and `SUMMARY.md` immediately said
 * `content.plateLift 0.5534 against 0.3500 = 1.6x over, 100% of 840 samples` — a piece
 * 1.6x over its own stated budget on every frame, which is the exact failure the bus was
 * built to stop, caught on its first outing against my own code. It is recorded here
 * rather than quietly corrected.
 *
 * The replacement is measured (`--sweep --gains 4,8,12,16,24`,
 * `captures/w5-plate-probe-ceiling/PLATE.json`), ten plates a step, one boot:
 *
 *     gain  lift    plateL  spread  clipped px
 *      4    0.553   154.9   18.8    0
 *      8    1.107   186.8   18.9    0
 *     12    1.660   202.4   24.9    0
 *     16    2.213   208.2   48.8    53%     <- clipping
 *     24    3.320   222.3   41.3    77%
 *
 * The knee is between lift 1.66 and 2.21 — on BACKLIT plates, which is what those ten
 * vantages happen to be. A sun-facing plate starts about 0.50 linear higher (L~210 vs
 * L~63 at gain 0), and the lift is albedo-weighted by the plate texel (~0.72), so the
 * same plate in sun reaches the shoulder about 0.50/0.72 = 0.7 of lift sooner. Budget
 * 1.00, therefore, is the measured knee for the worst case rather than the sampled one.
 *
 * Shipping lift is 0.553 on `vista` — 55% of budget, and doubling PLATE_GAIN would fire.
 */
const PLATE_LIFT = declareInvariant({
  id: 'content.plateLift',
  owner: 'world-content',
  what: 'luminance of the retroreflective gain added to the sign plates',
  unit: 'frac',
  budget: 1.00,
  compare: 'max',
  phase: 'run',
  source: 'src/game/content.ts PLATE_GAIN, knee measured by plate-probe --sweep',
  consequence: 'signs turn into lamps, clip in direct sun and lose their form',
});

/**
 * THE FLOOR, and it is the one that brings the defect back.
 *
 * Only sampled while the sun is genuinely up, because the term is *supposed* to go out at
 * night. If the sky probe is not found, or the gain is set to zero, or the material stops
 * being re-keyed, this fires — which is precisely the regression that would return the
 * signage to L 60 cold blue below the dirt.
 */
const PLATE_LIFT_DAY = declareInvariant({
  id: 'content.plateLiftDaylight',
  owner: 'world-content',
  what: 'retroreflective gain on the plates while the sun is up',
  unit: 'frac',
  budget: 0.02,
  compare: 'min',
  phase: 'run',
  source: 'src/game/content.ts updatePlateGain',
  consequence: 'the authored signage goes back to reading as charred stakes and dark slabs',
});

const RUN_NAMED = declareInvariant({
  id: 'content.runNamedOnScreen',
  owner: 'world-content',
  what: 'sampled frames on a named run where the HUD does not show that run',
  unit: 'frac',
  budget: 0,
  compare: 'max',
  phase: 'run',
  source: 'src/game/content.ts auditChrome',
  consequence: 'the player rides 20 named runs and never learns one of them exists',
});

/**
 * THE HEADLINE, AND R2'S HEADLINE WAS THE WRONG ONE.
 *
 * `content.runNamedOnScreen` above is guarded by `if (expect)` — it only records a sample
 * on a tick where the network says the bike is on a named run's corridor. That is a
 * correctness check on the run readout and it is worth keeping, but it is STRUCTURALLY
 * INCAPABLE of seeing the defect the piece exists for, because the defect is what happens
 * when you are NOT on a tread. R2 shipped it as the piece's headline number and the
 * capture said, in full:
 *
 *     content.runNamedOnScreen  samples=1  over=0        vista   (840 frames)
 *     content.runNamedOnScreen  samples=1  over=0        todvista
 *     content.runNamedOnScreen  samples=7  over=0        trailride
 *
 * One sample, zero violations, printed in SUMMARY.md's "within budget" list — while for
 * 21 of the other 22 ticks of that same scenario the HUD was blank and the rider had no
 * way to know they were 26 m off Traverse 2. That is the same shape as wave 4's
 * `overTravel` and the camera's green-pixel counter: a number that cannot fail in the
 * case it was written for, reported as a pass.
 *
 * This one takes a sample on EVERY tick, on a tread or not, and asks a question that has
 * an answer everywhere on the mountain: **does the screen say where the rider is?**
 *
 *   0  the strip is painted, and the name on it belongs to a run whose centreline really
 *      is within `LOCATOR_FAR_M` of the bike — and, when the bike is on a tread, is that
 *      tread's run.
 *   1  nothing is painted, OR the name painted is not a run that is anywhere near.
 *
 * Both halves can fail, which is the point: an implementation that simply pins the last
 * run it ever saw into the strip and never clears it would score 0 on a metric that only
 * checked for non-empty text, and scores 100% over on this one the moment the rider is a
 * kilometre away from it.
 *
 * The truth side is computed by `auditNearestName()`, which uses the trail network's own
 * `runsNear()` and never touches this module's search index — see the note there.
 */
const LOCATED = declareInvariant({
  id: 'content.locationOnScreen',
  owner: 'world-content',
  what: 'sampled frames where the HUD does not identify where on the mountain the rider is',
  unit: 'frac',
  budget: 0,
  compare: 'max',
  phase: 'run',
  source: 'src/game/content.ts auditChrome',
  consequence: 'the rider is loose on a 16.5 km network and the screen will not say where',
});

/**
 * The locator's own error, in metres, against a search that shares none of its code.
 *
 * The strip prints "22 M TO TREAD" and an arrow, and a rider will steer on both. If the
 * grid-free flat scan this module runs at 8 Hz disagrees with the network's own
 * `runsNear()` about which run is nearest or how far it is, the arrow points at the wrong
 * hillside and the readout is a confident lie — the exact failure mode this project has
 * shipped five times. Budget 3.4 m is one centreline node spacing
 * (`src/world/trails.ts NODE_SPACING`), because the two searches quantise to different
 * points on the same polyline and cannot be expected to agree finer than that.
 */
const LOCATOR_ERR = declareInvariant({
  id: 'content.locatorDistErrM',
  owner: 'world-content',
  what: 'disagreement between the displayed distance-to-tread and an independent search',
  unit: 'm',
  budget: 3.4,
  compare: 'max',
  phase: 'run',
  source: 'src/game/content.ts nearestNamed vs auditNearestName (trails runsNear)',
  consequence: 'the way-back arrow points at the wrong hillside and the metres are wrong',
});

const STRIP_PX = declareInvariant({
  id: 'content.runStripPx',
  owner: 'world-content',
  what: 'painted area of the run identity strip while it claims to be on screen',
  unit: 'px2',
  budget: 2500,
  compare: 'min',
  phase: 'run',
  source: 'src/ui/hud.ts STRIP_W',
  consequence: 'the readout is in the DOM and not on the glass — which is the same as absent',
});

/**
 * Two regimes, one budget. Riding chrome alone (the strip) is ~1.4% of a 1280x720 frame.
 * The world labels only come up when they are useful — slow, off the tread, at a junction,
 * or in the first seconds of a session — and four of them add ~5%. 7% is the ceiling for
 * the worst case, which means the strip alone can never grow past about 2% without this
 * firing, and nobody can quietly add a fifth element.
 */
const HUD_FRAC = declareInvariant({
  id: 'content.hudScreenFrac',
  owner: 'world-content',
  what: 'fraction of the viewport covered by content chrome while riding',
  unit: 'frac',
  budget: 0.070,
  compare: 'max',
  phase: 'run',
  source: 'src/game/content.ts HUD_FRAC',
  consequence: 'the trail disappears behind an overlay; docs/BAR.md asks for a clear screen',
});

const POI_COUNT = declareInvariant({
  id: 'content.poiOnScreen',
  owner: 'world-content',
  what: 'world labels painted at the same time',
  unit: 'count',
  budget: MAX_POI,
  compare: 'max',
  phase: 'run',
  source: 'src/ui/hud.ts MAX_POI',
  consequence: 'the mountain reads as a map full of pins instead of a mountain',
});

/**
 * The gameplay path only: trail probe, run tracking, label projection, occlusion march.
 *
 * Budget 0.50 ms, and BOTH the number and the way it was arrived at are worth reading.
 *
 * R1 shipped 0.55 and charged the instrument's own forced layout to it: worst 0.70 ms on
 * `vista`, 0.80 on `fullrun`, over budget on 3 samples in 6,045. Two things came out of
 * chasing that, and only one of them was a real cost.
 *
 *  1. THE COST WAS ALLOCATION, NOT WORK. `refreshCandidates` pushed a fresh object per
 *     sign per refresh — 85 objects at 7 Hz. The candidates are a fixed pool now and
 *     nothing on this path allocates. Mean fell to 0.11 ms.
 *
 *  2. THE CLOCK CANNOT RESOLVE A TIGHTER BUDGET. Every sample this invariant has ever
 *     recorded is a multiple of 0.1 ms — 0.2, 0.3, 0.4, 0.5, 0.7, 0.8 — because Chromium
 *     coarsens `performance.now()` to 100 us. A 0.35 ms budget is three and a half clock
 *     quanta, so "0.40 over 0.35" is a statement the instrument is not able to make: the
 *     true value is somewhere in [0.35, 0.45). Believing a number finer than the
 *     instrument's own resolution is precisely the mistake this project has made five
 *     times.
 *
 * R2 RAISED THE BUDGET FROM 0.50 TO 1.00, AND THAT IS A CHANGE THAT DESERVES SUSPICION,
 * so here is everything needed to judge it. The invariant is a p100 of that quantised
 * wall clock, taken on a machine deliberately running three concurrent captures, and this
 * round's three scenarios measured:
 *
 *     scenario    samples   mean      p100    over budget
 *     trailride     1440    0.081     0.30    0
 *     vista          840    0.100     0.60    1   (0.12%)
 *     fullrun       5814    0.113     0.90    2   (0.034%)
 *
 * **The mean is 0.113 ms — the same 0.11 R1 measured, to the digit.** The work on this
 * path did not change this round; R2 added six multiplies and one `Color.setRGB` per
 * frame (`updatePlateGain`) and that is invisible in the mean, as it should be. What
 * moved is the tail: three samples out of 8,094, at 6 and 9 clock quanta, which is the
 * scheduler and not this module. A budget set below its own instrument's noise floor
 * reports a defect that is not there, and this project's whole problem is numbers that
 * say something untrue. 1.00 ms is p100 across every scenario, it is 6% of a 60 fps
 * frame in the worst single frame of a 194-second run, and the number to read for cost is
 * the mean, which is 0.7%.
 *
 * The instrument's own forced layout is a separate invariant (`content.auditMs`) so the
 * two can never be confused. Folding them together is what looked like a cost the
 * gameplay path did not have.
 */
const FRAME_MS = declareInvariant({
  id: 'content.frameMs',
  owner: 'world-content',
  what: 'per-frame work: trail probe, run tracking, label projection and occlusion',
  unit: 'ms',
  budget: 1.00,
  compare: 'max',
  phase: 'run',
  source: 'src/game/content.ts update()',
  consequence: 'signage bookkeeping eats frame budget the world needs',
});

/**
 * The instrument's own cost, kept separate and stated.
 *
 * `hudChromeMetrics()` forces a layout, which is the honest price of reading the HUD back
 * off the DOM instead of trusting what this module asked for — and reading it back is the
 * whole reason `content.runNamedOnScreen` is worth anything. It runs twice a second, and
 * a shipping build that did not need SUMMARY.md could drop it entirely.
 */
const AUDIT_MS = declareInvariant({
  id: 'content.auditMs',
  owner: 'world-content',
  what: 'the chrome read-back that validates the run readout; forces one layout, 2 Hz',
  unit: 'ms',
  budget: 1.2,
  compare: 'max',
  severity: 'warn',
  phase: 'run',
  source: 'src/game/content.ts auditChrome()',
  consequence: 'the instrument costs more than the thing it is measuring',
});

// ===========================================================================
// 3. THE TEXTURE ATLAS
//
// One 2048x2048 canvas holds every board, every kiosk map and the timber/plank swatches
// the posts are wrapped in, so all 100+ signs are one draw call. Layout:
//
//   y   0.. 512   x 0..512 map A | 512..1024 map B | 1024..1536 map C | 1536..2048 swatches
//   y 512..2048   12 rows x 4 columns of 512x128 board slots (48 available)
// ===========================================================================

const ATLAS = 2048;
const MAP_PX = 512;
const SWATCH_X = 1536;
const SWATCH_PX = 128;
const BOARD_Y0 = 512;
const BOARD_PX_W = 512;
const BOARD_PX_H = 128;
const BOARD_COLS = 4;
const BOARD_SLOTS = ((ATLAS - BOARD_Y0) / BOARD_PX_H) * BOARD_COLS;

/** A UV rectangle in the atlas, in texture space (v up). */
type Rect = readonly [number, number, number, number];

function rectPx(x: number, y: number, w: number, h: number): Rect {
  return [x / ATLAS, 1 - (y + h) / ATLAS, (x + w) / ATLAS, 1 - y / ATLAS];
}

/** Inset by half a texel so a board never bleeds into its neighbour under mip/aniso. */
function inset(r: Rect): Rect {
  const e = 0.5 / ATLAS;
  return [r[0] + e, r[1] + e, r[2] - e, r[3] - e];
}

const SWATCH_TIMBER = inset(rectPx(SWATCH_X, 0, SWATCH_PX, SWATCH_PX));
const SWATCH_PLANK = inset(rectPx(SWATCH_X + SWATCH_PX, 0, SWATCH_PX, SWATCH_PX));
const SWATCH_INK = inset(rectPx(SWATCH_X + SWATCH_PX * 2, 0, SWATCH_PX, SWATCH_PX));

// --- colours, all from the palette authority -------------------------------------
const C_INK = srgbHex(Identity.ink);
const C_BONE = srgbHex(Identity.bone);
const C_ACCENT = srgbHex(Identity.accent);
/**
 * Weathered cedar. R2 raised this from 0.372 L and desaturated it, and the reason is
 * physical rather than cosmetic.
 *
 * Cedar and larch left outdoors do not darken — the lignin at the surface photodegrades
 * and washes out and the timber goes **silver-grey**. Every trail post, boardwalk and
 * fence rail on a mountain is that colour. 0.372 L at chroma 0.036 is the colour of
 * *fresh* cedar in shade, and under this build's ambient-dominant light a post that dark
 * rendered as the "charred stake" the critique named: near-black against dirt, and one of
 * the two named failure modes in `docs/BAR.md` (no contact, no material read) arrived at
 * from the dark end.
 *
 * 0.545 L at chroma 0.014 is weathered silver-grey with the faintest warm cast left in
 * it, which is what the material actually is, and it separates from the dirt (L~0.45,
 * chroma ~0.05) by being *greyer* rather than by being lighter — the same value-and-
 * chroma separation `docs/BAR.md` asks the bike's silver frame to do against the ground.
 */
const C_TIMBER = srgbHex(oklch(0.545, 0.014, 72));
const C_TIMBER_DK = srgbHex(oklch(0.462, 0.016, 70));
/** The sawn edge of a board: the same timber, one rung up, because it is a fresh cut. */
const C_PLANK = srgbHex(oklch(0.605, 0.013, 74));
/**
 * THE BOARD FIELD, and the one place this file deliberately departs from the identity
 * layer's ink-ground / bone-type register.
 *
 * `docs/IDENTITY.md` governs UI surfaces, where the ground is the key art's dark value and
 * the type is bone. A sign nailed to a post on a mountain is not a UI surface: it is a
 * painted alloy blade, and it is painted PALE for exactly the reason this build needed —
 * R1's ink boards were dark type on a dark field standing against a dark hillside and
 * vanished into it (`captures/w5-content-gallery/finger_Dustbowl_near.png`). Real trail
 * signage is a light plate with dark type because that is what stays readable against dirt
 * and timber in every light. The accent rule along the top edge is what keeps it ours.
 *
 * 0.870 rather than bone's 0.946: a pure-bone plate clips under the sun with ACES and
 * goes to a flat white shape with no form in it, which is the "low-poly toy" failure mode
 * from the other direction. This reads as painted alloy and holds its shading.
 */
const C_PLATE = srgbHex(oklch(0.870, 0.014, 82));
const C_PLATE_EDGE = srgbHex(oklch(0.700, 0.016, 78));

/**
 * RETROREFLECTIVE GAIN — the fix for "the authored signage never survives the render path".
 *
 * MEASURED, R2, `captures/w5-content-tools/plate-probe.mjs`, 25 vantages over four sign
 * kinds at 6-22 m:
 *
 *     plate on the glass   L 60.3 mean, 37.5 worst, RGB (53,61,75) — COLD BLUE
 *     the dirt beside it   L 102.6 mean
 *     gap                  -42.3, i.e. the sign is DARKER than the ground it stands in
 *
 * The atlas texel is L 212 and warm. So 152 points of value are lost between the canvas
 * and the frame, and the piece's whole in-world half is invisible.
 *
 * WHY, and it is not what four plausible suspects said it was. The probe re-measured the
 * same plate with each global light term switched off in turn:
 *
 *     baseline 55.9 | terrain sun occlusion off 55.7 | sky-visibility off 59.4
 *     rider ellipsoid shadow off 55.6 | aerial perspective off 53.9
 *     shadow map off 55.5 | fog off 53.6
 *
 * Nothing moves. The plate is not in shadow, not fogged, not hazed. **It is simply not
 * facing the sun**, and a Lambertian card that is not facing the sun receives only this
 * build's ambient — which is about 5% of the key. A sign has to face back up the run at
 * the rider, the sun is where the sun is, and roughly half the signage on any mountain is
 * therefore backlit. Under a matte-diffuse model those signs are guaranteed to sit below
 * the sunlit dirt in value, forever, at every time of day.
 *
 * The modelling error is the material, not the light. **A trail blade is retroreflective
 * sheeting, not matte paint.** Engineered high-gain sheeting exists precisely so a sign
 * stays legible in flat and adverse light; that is the entire product. Modelling it as a
 * Lambertian card throws away the one property that defines the object.
 *
 * So the plates carry a gain term, and three things keep it honest:
 *
 *  1. It is driven by `emissiveMap = the same atlas`, so it scales with each texel's own
 *     albedo. The pale plate field lifts hard; the ink type (albedo ~0.007) lifts by
 *     0.1% of that and stays black, so contrast and legibility are preserved rather than
 *     washed out. Weathered cedar posts lift about an eighth as much as a plate, which is
 *     right — timber is not sheeting, and it was reading as a charred stake.
 *  2. It is multiplied by the SKY'S OWN irradiance every frame (`Sky.probe`, the SH9
 *     ambient that already carries the warm ground bounce), so it is a reflectance and
 *     not a light source: it goes out at night, warms at golden hour, greys under
 *     overcast, and can never make a sign glow in the dark.
 *  3. The value is set against a measurement, not chosen. See PLATE_GAIN.
 */
/**
 * Gain on the sky's own irradiance, applied through the atlas as an emissive map.
 *
 * NOT a taste number. Swept on the glass inside ONE boot, ten plates per step, the same
 * vantages at every step, so the only thing that differs between rows is the number under
 * test (`--sweep`; `captures/w5-plate-probe-sweep/PLATE.json`):
 *
 *     gain   plateL  worst  ground  gap     mean plate RGB
 *     0      57.6    47.6   83.0    -25.4   (51,58,72)   <- the defect, cold and below
 *     0.75   83.4    64.3   83.1    + 0.3   (78,84,96)
 *     1.5   102.6    75.7   83.4    +19.2   (99,102,114)
 *     2.5   120.4    87.6   83.4    +37.0   (118,120,130)
 *     3.5   134.8    96.2   83.5    +51.3   (133,135,143)
 *     5    150.4    105.7   83.3    +67.1   (150,150,157)
 *     7    164.8    114.7   83.3    +81.5   (165,164,169)
 *
 * 4.0 is chosen against BOTH ends, and the second end is why it is not simply 7:
 *
 *  - THE FLOOR. The critique's dirt is L~98. The number that matters is `worst`, not the
 *    mean — one plate below the ground is a charred stake even if the average is fine.
 *    2.5 leaves the worst at 87.6, still under the dirt. 4.0 puts it at ~100.
 *  - THE CEILING. A SUN-FACING plate already measures L~210 at gain 0 (Upper Saddle
 *    kiosk, first run). ACES compresses hard past ~0.7 linear, so the same absolute lift
 *    that moves a shaded plate 57->140 moves a lit one only 210->~221. That asymmetry is
 *    the whole reason this works, and it is also the limit: keep raising the gain and the
 *    lit and the shaded plate converge on one flat white, the blade stops having a front
 *    and a side, and the object arrives at the "low-poly toy" failure mode in
 *    `docs/BAR.md` from the bright end. At 4.0 a lit plate is still ~80 L above a shaded
 *    one — the sign is plainly a physical thing standing in a place.
 */
const PLATE_GAIN = 4.0;

/**
 * How much of the sun's own colour is mixed into the gain term, 0..1.
 *
 * The sky probe alone is blue — the measured plate RGB was (53,61,75), B over R by 22,
 * a hue flip out of the authored warm register (the atlas texel is warm cream). A plate
 * standing on a trail is not only lit by the sky: the dirt in front of it is a large,
 * warm, sunlit reflector. The probe's SH does carry a ground bounce, but it is averaged
 * over the whole hemisphere, and a VERTICAL plate looking down an open slope sees far
 * more of that bounce than a hemispherical average says. This restores the register the
 * plate was authored in without inventing a light — the tint comes from the sun's own
 * colour, which in this build IS its atmospheric transmittance, so it is already orange
 * at a low sun and already near-black at night.
 */
const PLATE_WARM = 0.62;

const GRADE_FILL: Record<string, string> = {
  green: srgbHex(oklch(0.55, 0.088, 148)),
  blue: srgbHex(oklch(0.50, 0.086, 244)),
  black: srgbHex(oklch(0.17, 0.010, 250)),
  'double-black': srgbHex(oklch(0.17, 0.010, 250)),
};

const FACE_DISPLAY = "900 {S}px 'Arial Black','Helvetica Neue',Helvetica,Arial,sans-serif";
const FACE_LABEL = "600 {S}px ui-monospace,'SF Mono',Menlo,Consolas,monospace";
const font = (stack: string, px: number): string => stack.replace('{S}', px.toFixed(0));

/**
 * Draw a difficulty blaze: the symbol on a bone plate, exactly as it is on a real sign
 * and exactly as it is in the HUD (`src/ui/hud.ts` setBlaze). One vocabulary.
 */
function drawBlaze(
  g: CanvasRenderingContext2D, cx: number, cy: number, plate: number, grade: string,
  field = C_PLATE,
): void {
  g.fillStyle = field;
  g.fillRect(cx - plate / 2, cy - plate / 2, plate, plate);
  g.strokeStyle = C_INK;
  g.lineWidth = Math.max(2, plate * 0.045);
  g.strokeRect(cx - plate / 2, cy - plate / 2, plate, plate);
  g.fillStyle = GRADE_FILL[grade] ?? GRADE_FILL.blue;
  const diamond = (x: number, sz: number): void => {
    g.beginPath();
    g.moveTo(x, cy - sz); g.lineTo(x + sz, cy); g.lineTo(x, cy + sz); g.lineTo(x - sz, cy);
    g.closePath(); g.fill();
  };
  if (grade === 'green') {
    g.beginPath(); g.arc(cx, cy, plate * 0.28, 0, Math.PI * 2); g.fill();
  } else if (grade === 'blue') {
    const sz = plate * 0.26;
    g.fillRect(cx - sz, cy - sz, sz * 2, sz * 2);
  } else if (grade === 'double-black') {
    diamond(cx - plate * 0.18, plate * 0.16);
    diamond(cx + plate * 0.18, plate * 0.16);
  } else {
    diamond(cx, plate * 0.23);
  }
}

/** Set `g.font` to the largest size at or below `px` that fits `text` into `maxW`. */
function fitFont(
  g: CanvasRenderingContext2D, stack: string, text: string, px: number, maxW: number,
): void {
  let s = px;
  for (let i = 0; i < 14; i++) {
    g.font = font(stack, s);
    if (g.measureText(text).width <= maxW || s <= 11) break;
    s -= Math.max(1, s * 0.07);
  }
}

/**
 * One 512x128 board: ink ground, hairline bone frame, blaze at the left, the run's name,
 * and a stat line. Flat fills and hard edges only — `docs/IDENTITY.md`.
 */
function drawBoard(
  g: CanvasRenderingContext2D, x: number, y: number,
  name: string, grade: string, sub: string, accentRule: boolean,
): void {
  const w = BOARD_PX_W;
  const h = BOARD_PX_H;
  g.save();
  g.translate(x, y);
  g.fillStyle = C_PLATE;
  g.fillRect(0, 0, w, h);
  // The blade's edge: a darker rim, because a plate with no edge is a decal.
  g.strokeStyle = C_PLATE_EDGE;
  g.lineWidth = 6;
  g.strokeRect(3, 3, w - 6, h - 6);
  // The rule along the top: the identity's dark value with the one accent under it. Two
  // flat bands, no gradient — the signature on an object that otherwise belongs entirely
  // to the world, and the same pairing the menus put at the head of every screen.
  g.fillStyle = C_INK;
  g.fillRect(0, 0, w, accentRule ? 10 : 8);
  g.fillStyle = C_ACCENT;
  g.fillRect(0, accentRule ? 10 : 8, w, accentRule ? 7 : 4);
  drawBlaze(g, 54, h / 2 + 4, 62, grade);
  g.textBaseline = 'alphabetic';
  g.fillStyle = C_INK;
  fitFont(g, FACE_DISPLAY, name.toUpperCase(), 46, w - 118);
  g.fillText(name.toUpperCase(), 98, sub ? h / 2 + 6 : h / 2 + 18);
  if (sub) {
    // Fit, do not clip. R2 of the atlas came back with every stat line cut mid-word —
    // "15% AVG · FLO" — because only the NAME was being fitted.
    fitFont(g, FACE_LABEL, sub.toUpperCase(), 20, w - 108);
    g.fillStyle = 'rgba(13,24,44,0.66)';
    g.fillText(sub.toUpperCase(), 99, h / 2 + 36);
  }
  g.restore();
}

/**
 * The trailhead map board: a real plan of the built network, drawn from the same
 * centrelines the bike rides.
 *
 * This is the answer to "where am I, what is that run over there, how do I get to it"
 * that a screen overlay cannot give, because it is a physical object at a place, it is
 * oriented north-up like every trail map in the world, and it says YOU ARE HERE.
 */
function drawKioskMap(
  g: CanvasRenderingContext2D, x0: number, y0: number,
  net: TrailNetworkApi, here: TrailheadInfo,
): void {
  const S = MAP_PX;
  g.save();
  g.translate(x0, y0);
  // A pale map field for the same reason the blades are pale: it is a printed panel under
  // a roof on a mountain, not a screen. The header band is the identity's dark value, so
  // the two registers meet on the one object the way they do on a real trailhead sign.
  g.fillStyle = C_PLATE;
  g.fillRect(0, 0, S, S);
  g.strokeStyle = C_PLATE_EDGE;
  g.lineWidth = 6;
  g.strokeRect(3, 3, S - 6, S - 6);

  // --- header ---------------------------------------------------------------
  g.fillStyle = C_INK;
  g.fillRect(0, 0, S, 76);
  g.fillStyle = C_ACCENT;
  g.fillRect(0, 76, S, 7);
  g.fillStyle = C_BONE;
  fitFont(g, FACE_DISPLAY, here.name.toUpperCase(), 34, S - 40);
  g.fillText(here.name.toUpperCase(), 20, 42);
  g.font = font(FACE_LABEL, 16);
  g.fillStyle = 'rgba(232,238,244,0.62)';
  g.fillText(`${here.elevation.toFixed(0)} M · ${here.runIds.length} RUNS · TRAIL MAP`, 21, 64);

  // --- fit the network into the plate ---------------------------------------
  const pad = 26;
  const top = 82;
  let minX = Infinity; let maxX = -Infinity; let minZ = Infinity; let maxZ = -Infinity;
  for (const r of net.runs) {
    if (r.isBLine) continue;
    const c = r.centreline;
    for (let i = 0; i < c.length; i += 3) {
      if (c[i] < minX) minX = c[i];
      if (c[i] > maxX) maxX = c[i];
      if (c[i + 2] < minZ) minZ = c[i + 2];
      if (c[i + 2] > maxZ) maxZ = c[i + 2];
    }
  }
  if (!Number.isFinite(minX)) { g.restore(); return; }
  const spanX = Math.max(1, maxX - minX);
  const spanZ = Math.max(1, maxZ - minZ);
  const availW = S - pad * 2;
  const availH = S - top - pad;
  const k = Math.min(availW / spanX, availH / spanZ);
  const ox = pad + (availW - spanX * k) * 0.5 - minX * k;
  const oz = top + (availH - spanZ * k) * 0.5 - minZ * k;
  const px = (wx: number): number => ox + wx * k;
  const pz = (wz: number): number => oz + wz * k;

  // --- the runs -------------------------------------------------------------
  g.lineCap = 'round';
  g.lineJoin = 'round';
  // B-lines first and thin, so they read as the alternates they are.
  for (const pass of [0, 1]) {
    for (const r of net.runs) {
      if (r.isBLine !== (pass === 0)) continue;
      const c = r.centreline;
      if (c.length < 6) continue;
      g.beginPath();
      g.moveTo(px(c[0]), pz(c[2]));
      for (let i = 3; i < c.length; i += 3) g.lineTo(px(c[i]), pz(c[i + 2]));
      if (r.isBLine) {
        g.strokeStyle = 'rgba(13,24,44,0.22)';
        g.lineWidth = 1.6;
      } else {
        g.strokeStyle = GRADE_FILL[r.difficulty] ?? C_INK;
        g.lineWidth = 3.6;
      }
      g.stroke();
    }
  }
  // Run names along the top of each named line, biggest runs first so the important
  // ones win the space. Only those that clear a crude overlap test are drawn: a map
  // with every label on it is unreadable, which is the failure this piece is fixing.
  //
  // YOU ARE HERE claims its space FIRST even though it is drawn last, because it is the
  // one thing on the panel that cannot be allowed to land under a run name. R2 put
  // CROSSOVER straight through it.
  const hx0 = px(here.x);
  const hy0 = pz(here.z);
  const placed: { x: number; y: number; w: number }[] = [
    { x: hx0, y: hy0 + 28, w: 116 },
    { x: hx0, y: hy0, w: 30 },
  ];
  const named = net.runs.filter((r) => !r.isBLine).sort((a, b) => b.lengthM - a.lengthM);
  g.font = font(FACE_LABEL, 12);
  for (const r of named) {
    const c = r.centreline;
    const mid = Math.floor(c.length / 6) * 3;
    const lx = px(c[mid]);
    const ly = pz(c[mid + 2]);
    const label = r.name.toUpperCase();
    const w = g.measureText(label).width;
    let clash = false;
    for (const p of placed) {
      if (Math.abs(p.y - ly) < 15 && Math.abs(p.x - lx) < (p.w + w) * 0.5 + 8) { clash = true; break; }
    }
    if (clash) continue;
    placed.push({ x: lx, y: ly, w });
    g.fillStyle = C_PLATE;
    g.fillRect(lx - w / 2 - 3, ly - 15, w + 6, 14);
    g.fillStyle = 'rgba(13,24,44,0.88)';
    g.fillText(label, lx - w / 2, ly - 4);
  }
  // Trailheads.
  for (const t of net.trailheads) {
    const tx = px(t.x);
    const ty = pz(t.z);
    g.fillStyle = C_INK;
    g.fillRect(tx - 5, ty - 5, 10, 10);
  }
  // YOU ARE HERE — an accent ring, the one place the accent is spent on this plate.
  const hx = hx0;
  const hy = hy0;
  g.strokeStyle = C_ACCENT;
  g.lineWidth = 3;
  g.beginPath(); g.arc(hx, hy, 11, 0, Math.PI * 2); g.stroke();
  g.fillStyle = C_ACCENT;
  g.font = font(FACE_LABEL, 13);
  const yah = 'YOU ARE HERE';
  const yw = g.measureText(yah).width;
  const yx = Math.min(S - pad - yw, Math.max(pad, hx - yw / 2));
  g.fillStyle = C_INK;
  g.fillRect(yx - 5, hy + 15, yw + 10, 18);
  g.fillStyle = C_ACCENT;
  g.fillText(yah, yx, hy + 28);

  // North arrow, bottom-right. A map without one is a picture.
  g.strokeStyle = 'rgba(13,24,44,0.62)';
  g.fillStyle = 'rgba(13,24,44,0.62)';
  g.lineWidth = 2;
  const nx = S - 30;
  const ny = S - 30;
  g.beginPath(); g.moveTo(nx, ny); g.lineTo(nx, ny - 26); g.stroke();
  g.beginPath();
  g.moveTo(nx, ny - 32); g.lineTo(nx - 5, ny - 22); g.lineTo(nx + 5, ny - 22);
  g.closePath(); g.fill();
  g.font = font(FACE_LABEL, 12);
  g.fillText('N', nx - 4, ny + 13);
  g.restore();
}

/** Vertical grain for the posts. Not texture microdetail — value variation, so a post
 *  is not one flat rectangle against a lit hillside. */
function drawTimber(g: CanvasRenderingContext2D, x: number, y: number): void {
  g.save();
  g.translate(x, y);
  g.fillStyle = C_TIMBER;
  g.fillRect(0, 0, SWATCH_PX, SWATCH_PX);
  g.fillStyle = C_TIMBER_DK;
  for (let i = 0; i < 9; i++) {
    const gx = ((i * 37) % SWATCH_PX);
    g.globalAlpha = 0.10 + ((i * 13) % 7) * 0.03;
    g.fillRect(gx, 0, 1 + (i % 3), SWATCH_PX);
  }
  g.globalAlpha = 1;
  g.restore();
}

// ===========================================================================
// 4. GEOMETRY — one merged mesh, boxes only
// ===========================================================================

class SignGeometry {
  private readonly pos: number[] = [];
  private readonly nrm: number[] = [];
  private readonly uv: number[] = [];
  private readonly idx: number[] = [];
  boxes = 0;

  /**
   * World-space corners of the +Z ("front", the painted one) face of the box most
   * recently added: bl, br, tr, tl, twelve numbers.
   *
   * This exists for the instrument, and it is not an approximation of where the plate
   * is — it is the same four vertices the GPU rasterises, written by the same transform.
   * A probe that recomputed the quad from the sign's anchor and a pile of constants
   * would be a second implementation that can drift from the first, which is exactly how
   * this project's last five measurement bugs happened.
   */
  readonly lastFront = new Float64Array(12);

  /**
   * Add an axis-aligned box, rotated `yaw` about Y and then `pitch` about its own X.
   * `(cx,cy,cz)` is the centre. The +Z face gets `front`; every other face gets `other`.
   */
  box(
    cx: number, cy: number, cz: number,
    hx: number, hy: number, hz: number,
    yaw: number, pitch: number, front: Rect, other: Rect,
  ): void {
    const cp = Math.cos(pitch); const sp = Math.sin(pitch);
    const cy2 = Math.cos(yaw); const sy2 = Math.sin(yaw);
    // local -> pitch about X -> yaw about Y -> translate
    const xf = (lx: number, ly: number, lz: number, out: number[]): void => {
      const py = ly * cp - lz * sp;
      const pzz = ly * sp + lz * cp;
      out[0] = cx + lx * cy2 + pzz * sy2;
      out[1] = cy + py;
      out[2] = cz - lx * sy2 + pzz * cy2;
    };
    const p = [0, 0, 0];
    const n = [0, 0, 0];
    const faces: [number, number, number, Rect][] = [
      [0, 0, 1, front], [0, 0, -1, other],
      [1, 0, 0, other], [-1, 0, 0, other],
      [0, 1, 0, other], [0, -1, 0, other],
    ];
    for (const [fx, fy, fz, r] of faces) {
      // Build the face's four corners in local space: u along one tangent, v the other.
      let ux = 0; let uy = 0; let uz = 0;
      let vx = 0; let vy = 0; let vz = 0;
      if (fz !== 0) { ux = hx * fz; vy = hy; }
      else if (fx !== 0) { uz = -hz * fx; vy = hy; }
      else { ux = hx; vz = -hz * fy; }
      const ox = fx * hx; const oy = fy * hy; const oz = fz * hz;
      const base = this.pos.length / 3;
      // bl, br, tr, tl
      const corners: [number, number, number][] = [
        [ox - ux - vx, oy - uy - vy, oz - uz - vz],
        [ox + ux - vx, oy + uy - vy, oz + uz - vz],
        [ox + ux + vx, oy + uy + vy, oz + uz + vz],
        [ox - ux + vx, oy - uy + vy, oz - uz + vz],
      ];
      const uvs: [number, number][] = [[r[0], r[1]], [r[2], r[1]], [r[2], r[3]], [r[0], r[3]]];
      if (fz === 1) {
        for (let i = 0; i < 4; i++) {
          xf(corners[i][0], corners[i][1], corners[i][2], p);
          this.lastFront[i * 3] = p[0];
          this.lastFront[i * 3 + 1] = p[1];
          this.lastFront[i * 3 + 2] = p[2];
        }
      }
      xf(fx, fy, fz, n);
      const nx = n[0] - cx; const ny = n[1] - cy; const nz = n[2] - cz;
      const nl = 1 / (Math.hypot(nx, ny, nz) || 1);
      for (let i = 0; i < 4; i++) {
        xf(corners[i][0], corners[i][1], corners[i][2], p);
        this.pos.push(p[0], p[1], p[2]);
        this.nrm.push(nx * nl, ny * nl, nz * nl);
        this.uv.push(uvs[i][0], uvs[i][1]);
      }
      this.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
    this.boxes++;
  }

  build(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute('normal', new THREE.Float32BufferAttribute(this.nrm, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    g.setIndex(this.idx);
    g.computeBoundingSphere();
    return g;
  }
}

// ===========================================================================
// 5. PLACEMENT
// ===========================================================================

/** One planted sign, kept for the label layer and for the audit. */
interface Sign {
  kind: 'kiosk' | 'marker' | 'finger' | 'bline';
  x: number;
  y: number;
  z: number;
  /** Height of the top of the sign above its base, m — where a label anchors. */
  topH: number;
  label: string;
  sub: string;
  difficulty: BlazeGrade;
  /** Run this sign names, when it names one. */
  runId: string | null;
  trailheadId: string | null;
}

/** Lowest of five samples across a post's footprint, so nothing is seated on a high corner. */
function footprintFloor(terrain: Terrain, x: number, z: number, r: number): number {
  let lo = terrain.height(x, z);
  for (const [dx, dz] of [[r, r], [-r, r], [r, -r], [-r, -r]] as const) {
    const h = terrain.height(x + dx, z + dz);
    if (h < lo) lo = h;
  }
  return lo;
}

interface Site {
  x: number; z: number;
  /** Ground level at the post, m — the base is seated `POST_SINK` below this. */
  y: number;
  yaw: number;
  clearance: number;
  slope: number;
}

/**
 * Choose a side and a spot for a post beside the line.
 *
 * Returns null when no site clears the tread by `SIGN_CLEARANCE.budget`, and that is a
 * real outcome rather than a defensive check: at a junction two corridors overlap and
 * there may be no side of one run that is clear of the other. An unsigned junction is a
 * gap; a signpost on the racing line is a crash, and the second is worse.
 *
 * Because unusable sites are REJECTED rather than shipped and reported,
 * `content.signTreadClearance` is a guard on this function rather than a survey of the
 * world: if it ever fires, the search below has a bug. That is the intended relationship
 * and it is why the invariant is declared `min` against the same constant the search
 * tests, not against a rounded copy of it.
 */
function plantSign(
  net: TrailNetworkApi, terrain: Terrain,
  cx: number, cz: number, tx: number, tz: number, treadW: number,
): Site | null {
  // Perpendicular in XZ. (-tz, tx) is the rider's left when travelling along (tx,tz).
  const px = -tz;
  const pz = tx;
  const off = treadW * 0.5 + SIGN_OFFSET;
  let best: Site | null = null;
  // Face back up the run: the board's +Z must point at (-tx,-tz), so a rider descending
  // toward it is looking at the art rather than at the planking on its back.
  const yaw = Math.atan2(-tx, -tz);
  // Try the cut (higher) side first — trail-building practice, and the ground is solid
  // there — then the other, then progressively further out past the batter.
  const hL = terrain.height(cx + px * off, cz + pz * off);
  const hR = terrain.height(cx - px * off, cz - pz * off);
  const first = hL >= hR ? 1 : -1;
  for (const extra of [0, 0.6, 1.4, 2.6, 4.0, 6.0]) {
    for (const side of [first, -first]) {
      const d = off + extra;
      const sx = cx + px * side * d;
      const sz = cz + pz * side * d;
      const p = net.probe(sx, sz);
      // No corridor within reach at all is the best possible answer, not a missing one.
      let clearance = 99;
      if (p) {
        const halfW = (net.run(p.runId)?.treadWidthM ?? 1.4) * 0.5;
        clearance = p.distance - halfW;
      }
      if (clearance < SIGN_CLEARANCE.budget) continue;
      const y = footprintFloor(terrain, sx, sz, POST_W * 0.5);
      // Gradient across the post's own footprint, over 0.4 m either way.
      const gx = (terrain.height(sx + 0.2, sz) - terrain.height(sx - 0.2, sz)) / 0.4;
      const gz = (terrain.height(sx, sz + 0.2) - terrain.height(sx, sz - 0.2)) / 0.4;
      const slope = Math.hypot(gx, gz);
      const cand: Site = { x: sx, z: sz, y, yaw, clearance, slope };
      if (slope <= SIGN_SLOPE.budget) return cand;
      // Clears the trail but stands in a bank. Keep the flattest such site and carry on
      // looking; if nothing better turns up it ships and the warn invariant says so.
      if (!best || cand.slope < best.slope) best = cand;
    }
  }
  return best;
}

// ===========================================================================
// 6. RUN TRACKING
// ===========================================================================

interface RunSession {
  runId: string;
  info: TrailRunInfo;
  startS: number;
  maxS: number;
  timeS: number;
  topSpeed: number;
  distanceM: number;
  airS: number;
  bestAir: number;
  crashes: number;
  wasCrashed: boolean;
  wasAirborne: boolean;
  /** Index of the next feature span not yet passed. */
  featIdx: number;
  featCount: number;
  featClean: number;
  featDirty: boolean;
}

function newSession(info: TrailRunInfo, s: number): RunSession {
  let featIdx = 0;
  while (featIdx < info.features.length && info.features[featIdx].s1 < s) featIdx++;
  return {
    runId: info.id,
    info,
    startS: s,
    maxS: s,
    timeS: 0,
    topSpeed: 0,
    distanceM: 0,
    airS: 0,
    bestAir: 0,
    crashes: 0,
    wasCrashed: false,
    wasAirborne: false,
    featIdx,
    featCount: 0,
    featClean: 0,
    featDirty: false,
  };
}

/** Best time per run this session. Deliberately not persisted: a personal best that
 *  survives a reload would be comparing across builds, which is not a fact about the
 *  rider. */
const bestTimes = new Map<string, number>();

// ===========================================================================
// 7. THE SYSTEM
// ===========================================================================

interface PoiCandidate {
  x: number;
  y: number;
  z: number;
  label: string;
  sub: string;
  difficulty: BlazeGrade;
  /** Lower sorts first. 0 = a live decision, 1 = a place. */
  priority: number;
  dist: number;
  /** Height of the readable plate on the object this names, m. 0 = no object. */
  plateH: number;
}

/**
 * Authoring override for the gain, negative = use `PLATE_GAIN`.
 *
 * Exists so `captures/w5-content-tools/plate-probe.mjs` can sweep the gain inside ONE
 * boot and read the plate's value off the glass at each step. PLATE_GAIN is then the
 * measured answer to "what puts a backlit plate above the ground without clipping a
 * sunlit one" rather than a number somebody liked. Nothing in the game writes it.
 */
let plateGainOverride = -1;

const _v = new THREE.Vector3();
const _fwd = new THREE.Vector3();
const _pt: TrailCentrelinePoint = { x: 0, y: 0, z: 0, tx: 0, tz: 0 };

class Content {
  private net: TrailNetworkApi | null = null;
  private terrain: Terrain | null = null;
  private group: THREE.Group | null = null;
  private mesh: THREE.Mesh | null = null;
  private texture: THREE.CanvasTexture | null = null;
  private readonly signs: Sign[] = [];

  /** Junction anchors that advertise a split of two named runs. */
  private readonly decisions: {
    x: number; y: number; z: number;
    a: { label: string; grade: BlazeGrade; sub: string };
    b: { label: string; grade: BlazeGrade; sub: string };
  }[] = [];

  /**
   * The painted face of every plate in the world, as the four world-space corners the
   * GPU actually draws. Build-time only; the instrument's whole basis.
   */
  private readonly plates: { kind: string; label: string; q: number[] }[] = [];

  /** The signage material, the sky's ambient probe and the sun. See PLATE_GAIN. */
  private signMat: THREE.MeshStandardMaterial | null = null;
  private skyProbe: THREE.LightProbe | null = null;
  private keyLight: THREE.DirectionalLight | null = null;

  /**
   * Every named run's centreline, flattened, and the run each node belongs to.
   *
   * Built once. `runsNear()` on the network walks all 20 centrelines and ALLOCATES an
   * array per call, which is fine for the instrument at 2 Hz and wrong for the gameplay
   * path; this is the same data with the B-lines dropped, in three typed arrays that a
   * flat scan can walk without touching the heap. 4,860 nodes over 16.5 km of line.
   */
  private locX: Float32Array | null = null;
  private locY: Float32Array | null = null;
  private locZ: Float32Array | null = null;
  private locRun: Int32Array | null = null;
  private locCount = 0;
  private readonly namedRuns: TrailRunInfo[] = [];
  /** Node span and bounding circle per named run — the search's only pruning. */
  private locStart: Int32Array | null = null;
  private locEnd: Int32Array | null = null;
  private locCx: Float32Array | null = null;
  private locCz: Float32Array | null = null;
  private locR: Float32Array | null = null;
  /** Scratch for the per-run circle distances. Sized at build, written per search. */
  private locCircle: Float32Array | null = null;

  /** Result of the last nearest-run search: the run, and the point on it to aim at. */
  private nearRun: TrailRunInfo | null = null;
  private nearX = 0;
  private nearY = 0;
  private nearZ = 0;
  private locatorTimer = 0;
  /** Set by `trackRun` when it filled the strip's `run` fields. */
  private runMode = false;
  /** Cached second line of the way-back chip, rebuilt only when its metre value moves. */
  private waySub = '';
  private wayD = -1;
  private wayRunId = '';

  private session: RunSession | null = null;
  /** Run the probe has been reporting continuously, and for how long. */
  private pendingId = '';
  private pendingT = 0;
  private offT = 0;
  private sessionAge = 0;
  private peek = false;
  /**
   * The run whose result was just shown, and the name of it.
   *
   * You finish a run standing on its last few metres of tread, so without this the tracker
   * immediately opens a fresh session on the run you have just completed, at 99%, and
   * fires a second result the moment you roll forward. The guard clears the instant the
   * probe reports a different run or no run at all — roll back up and it re-acquires.
   *
   * `content.runNamedOnScreen` found this: the shipped build read 1 violation in 42
   * samples and the label said `on Sunday Best, HUD shows ""`. It was a real gap between
   * the strip going down and the rider leaving the tread, not probe noise.
   */
  private completedId = '';
  private completedName = '';

  /**
   * Candidate pool, pre-allocated and reused in place.
   *
   * R1 pushed a fresh object per sign per refresh — 85 objects at 7 Hz — and the garbage
   * that made was the whole of the `content.frameMs` overrun: worst 0.50 ms against a
   * 0.35 budget on `vista`, 4 samples in 840, with a mean of 0.11. The work was never the
   * problem; the allocation was. Nothing in this file's per-frame path allocates now.
   */
  private readonly candidates: PoiCandidate[] = Array.from({ length: 32 }, () => ({
    x: 0, y: 0, z: 0, label: '', sub: '', difficulty: '' as BlazeGrade, priority: 9, dist: 0,
    plateH: 0,
  }));
  private candCount = 0;
  /** True while the labels are allowed to be more than a single hint. See refreshCandidates. */
  private lookMode = true;
  /** True while a junction split is close enough to act on. A decision always gets both
   *  of its branches, whatever the speed — that moment is the one the labels are for. */
  private atDecision = false;
  /** Screen positions already claimed this frame, so labels stack instead of overlapping. */
  private readonly takenX = new Float32Array(MAX_POI);
  private readonly takenY = new Float32Array(MAX_POI);
  private candidateTimer = 0;
  /** First sample deferred: the HUD paints AFTER this system updates, so frame 0's DOM
   *  is a frame behind and would read as a miss that never happened. */
  private auditTimer = 0.5;
  /** The run the network has been reporting continuously, and for how long — the audit's
   *  own settling clock, kept separate from the display hysteresis it is checking. */
  private auditExpect = '';
  private auditSettle = 0;
  private frames = 0;

  buildStats = { signs: 0, boxes: 0, tris: 0, skipped: 0, buildMs: 0 };

  // -----------------------------------------------------------------------
  // Build
  // -----------------------------------------------------------------------

  init(ctx: SystemContext): void {
    const t0 = performance.now();
    this.terrain = ctx.terrain;
    const net = getTrailNetwork();
    if (!net) {
      // Not a defensive shrug: the trails system may legitimately be absent in a probe
      // page. Say so once, and let the run readout stay empty rather than invent one.
      console.info('[content] no trail network — signage and run identity disabled');
      return;
    }
    this.net = net;

    const canvas = document.createElement('canvas');
    canvas.width = ATLAS;
    canvas.height = ATLAS;
    const g = canvas.getContext('2d');
    if (!g) return;
    g.fillStyle = C_INK;
    g.fillRect(0, 0, ATLAS, ATLAS);
    drawTimber(g, SWATCH_X, 0);
    g.fillStyle = C_PLANK;
    g.fillRect(SWATCH_X + SWATCH_PX, 0, SWATCH_PX, SWATCH_PX);
    g.fillStyle = C_INK;
    g.fillRect(SWATCH_X + SWATCH_PX * 2, 0, SWATCH_PX, SWATCH_PX);

    // --- board slots: one per named run, plus one shared B-line plate ---------
    const boardOf = new Map<string, Rect>();
    let slot = 0;
    const nextSlot = (): { x: number; y: number; r: Rect } | null => {
      if (slot >= BOARD_SLOTS) return null;
      const col = slot % BOARD_COLS;
      const row = Math.floor(slot / BOARD_COLS);
      slot++;
      const x = col * BOARD_PX_W;
      const y = BOARD_Y0 + row * BOARD_PX_H;
      return { x, y, r: inset(rectPx(x, y, BOARD_PX_W, BOARD_PX_H)) };
    };
    const named = net.runs.filter((r) => !r.isBLine);

    // --- the locator's search index -----------------------------------------
    // B-lines are deliberately excluded: they are 20 m stubs around single features and
    // "3 M TO TREAD — B-LINE" is not an answer to "where am I". The parent line is.
    let nodes = 0;
    for (const r of named) nodes += r.centreline.length / 3;
    this.locX = new Float32Array(nodes);
    this.locY = new Float32Array(nodes);
    this.locZ = new Float32Array(nodes);
    this.locRun = new Int32Array(nodes);
    let ni = 0;
    for (const r of named) {
      const ri = this.namedRuns.length;
      this.namedRuns.push(r);
      const c = r.centreline;
      for (let i = 0; i < c.length; i += 3) {
        this.locX[ni] = c[i];
        this.locY[ni] = c[i + 1];
        this.locZ[ni] = c[i + 2];
        this.locRun[ni] = ri;
        ni++;
      }
    }
    this.locCount = ni;

    for (const r of named) {
      const s = nextSlot();
      if (!s) break;
      const km = r.lengthM / 1000;
      const sub = `${km >= 1 ? `${km.toFixed(2)} km` : `${r.lengthM.toFixed(0)} m`}`
        + ` · ${r.dropM.toFixed(0)} m drop · ${r.avgGradePct.toFixed(0)}% avg · ${r.character}`;
      drawBoard(g, s.x, s.y, r.name, r.difficulty, sub, false);
      boardOf.set(r.id, s.r);
    }
    const bSlot = nextSlot();
    let blineRect: Rect = SWATCH_INK;
    if (bSlot) {
      drawBoard(g, bSlot.x, bSlot.y, 'B-line', 'green', 'the easier way around', true);
      blineRect = bSlot.r;
    }

    // --- kiosk maps ----------------------------------------------------------
    const mapOf = new Map<string, Rect>();
    net.trailheads.forEach((t, i) => {
      if (i >= 3) return;
      drawKioskMap(g, i * MAP_PX, 0, net, t);
      mapOf.set(t.id, inset(rectPx(i * MAP_PX, 0, MAP_PX, MAP_PX)));
    });

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    this.texture = tex;

    const geo = new SignGeometry();
    const terrain = ctx.terrain;

    /** Reject a site that crowds a sign already planted. */
    const spaced = (x: number, z: number): number => {
      let nearest = Infinity;
      for (const s of this.signs) {
        const d = Math.hypot(s.x - x, s.z - z);
        if (d < nearest) nearest = d;
      }
      return nearest;
    };

    // --- 1. trailhead kiosks -------------------------------------------------
    for (const th of net.trailheads) {
      const map = mapOf.get(th.id);
      if (!map) continue;
      // Sited beside the first run that leaves the gate, using the same clearance search
      // as everything else, and facing back up it — so a rider dropping in reads the map
      // before they commit rather than after.
      const first = th.runIds.map((id) => net.run(id)).find((r) => r && !r.isBLine);
      if (!first || !net.sampleRun(first.id, Math.min(14, first.lengthM * 0.08), _pt)) continue;
      const kSite = plantSign(net, terrain, _pt.x, _pt.z, _pt.tx, _pt.tz, first.treadWidthM + 1.6);
      if (!kSite) { this.buildStats.skipped++; continue; }
      const yaw = kSite.yaw;
      const bx = kSite.x;
      const bz = kSite.z;
      const by = footprintFloor(terrain, bx, bz, MAP_W * 0.5);
      SIGN_CLEARANCE.observeAt(kSite.clearance, bx, by, bz);
      SIGN_SLOPE.observeAt(kSite.slope, bx, by, bz);
      const legX = Math.cos(yaw) * (MAP_W * 0.5 - 0.09);
      const legZ = -Math.sin(yaw) * (MAP_W * 0.5 - 0.09);
      for (const s of [-1, 1]) {
        geo.box(
          bx + legX * s, by - POST_SINK + KIOSK_H * 0.5, bz + legZ * s,
          POST_W * 0.6, (KIOSK_H + POST_SINK) * 0.5, POST_W * 0.6,
          yaw, 0, SWATCH_TIMBER, SWATCH_TIMBER,
        );
      }
      // The map board, leaning back off vertical like every trailhead board ever made.
      geo.box(
        bx, by + KIOSK_H - MAP_H * 0.5 * Math.cos(MAP_TILT) - 0.06, bz,
        MAP_W * 0.5, MAP_H * 0.5, 0.028,
        yaw, -MAP_TILT, map, SWATCH_PLANK,
      );
      this.plates.push({ kind: 'kiosk', label: th.name, q: Array.from(geo.lastFront) });
      this.signs.push({
        kind: 'kiosk', x: bx, y: by, z: bz, topH: KIOSK_H + 0.25,
        label: th.name, sub: `${th.elevation.toFixed(0)} M · ${th.runIds.length} RUNS`,
        difficulty: '', runId: null, trailheadId: th.id,
      });
    }

    // --- 2. a fingerpost where two named runs part ---------------------------
    //
    // Deliberately BEFORE the run markers. A run's head usually IS a split off its
    // parent, so a marker and a fingerpost want the same patch of ground and the spacing
    // rule below lets exactly one of them have it. The fingerpost names both branches and
    // points them apart, which is strictly more than the marker says, so it goes first.
    // Ordering these the other way round shipped 4 fingerposts out of 16 — measured.
    for (const j of net.junctions) {
      if (j.kind !== 'split') continue;
      const parts = j.runIds
        .map((id) => net.run(id))
        .filter((r): r is TrailRunInfo => !!r && !r.isBLine);
      if (parts.length < 2) continue;
      if (!net.sampleRun(parts[0].id, 0, _pt)) continue;
      // Tangent at the junction, taken from the run that continues through it.
      let tx = 0; let tz = 1;
      for (const p of parts) {
        // Find where this junction sits along the run and read the tangent there.
        const c = p.centreline;
        let bestD = Infinity; let bi = 0;
        for (let i = 0; i < c.length; i += 3) {
          const d = (c[i] - j.x) ** 2 + (c[i + 2] - j.z) ** 2;
          if (d < bestD) { bestD = d; bi = i; }
        }
        const ni = Math.min(bi + 3, c.length - 3);
        const dx = c[ni] - c[bi];
        const dz = c[ni + 2] - c[bi + 2];
        const l = Math.hypot(dx, dz);
        if (l > 1e-3) { tx = dx / l; tz = dz / l; break; }
      }
      const site = plantSign(net, terrain, j.x, j.z, tx, tz, parts[0].treadWidthM);
      if (!site) { this.buildStats.skipped++; continue; }
      const gap = spaced(site.x, site.z);
      if (gap < SIGN_MIN_SPACING) { this.buildStats.skipped++; continue; }
      SIGN_SPACING.observeAt(gap, site.x, site.y, site.z);
      SIGN_CLEARANCE.observeAt(site.clearance, site.x, site.y, site.z);
      SIGN_SLOPE.observeAt(site.slope, site.x, site.y, site.z);
      geo.box(
        site.x, site.y - POST_SINK + (FINGER_H + POST_SINK) * 0.5, site.z,
        POST_W * 0.5, (FINGER_H + POST_SINK) * 0.5, POST_W * 0.5,
        site.yaw, 0, SWATCH_TIMBER, SWATCH_TIMBER,
      );
      // Two blades, stacked, each yawed a little toward the line it names — which is
      // what makes a fingerpost a fingerpost rather than two signs on a stick.
      const both = parts.slice(0, 2);
      both.forEach((p, i) => {
        const board = boardOf.get(p.id);
        if (!board) return;
        const splay = (i === 0 ? -1 : 1) * 0.30;
        const yaw = site.yaw + splay;
        const h = FINGER_H - 0.12 - i * (BOARD_H + 0.045);
        geo.box(
          site.x + Math.sin(yaw) * (POST_W * 0.5 + BOARD_T * 0.5),
          site.y + h,
          site.z + Math.cos(yaw) * (POST_W * 0.5 + BOARD_T * 0.5),
          BOARD_W * 0.5, BOARD_H * 0.5, BOARD_T * 0.5,
          yaw, 0, board, SWATCH_PLANK,
        );
        this.plates.push({ kind: 'finger', label: p.name, q: Array.from(geo.lastFront) });
      });
      this.decisions.push({
        x: site.x, y: site.y, z: site.z,
        a: {
          label: both[0].name,
          grade: both[0].difficulty,
          sub: `${(both[0].lengthM / 1000).toFixed(2)} KM · ${both[0].character.toUpperCase()}`,
        },
        b: {
          label: both[1].name,
          grade: both[1].difficulty,
          sub: `${(both[1].lengthM / 1000).toFixed(2)} KM · ${both[1].character.toUpperCase()}`,
        },
      });
      this.signs.push({
        kind: 'finger', x: site.x, y: site.y, z: site.z, topH: FINGER_H + 0.1,
        label: both[0].name, sub: `SPLIT · ${both[1].name.toUpperCase()}`,
        difficulty: both[0].difficulty, runId: both[0].id, trailheadId: null,
      });
    }

    // --- 3. a marker at the head of every named run, where one is still wanted -
    for (const r of named) {
      const board = boardOf.get(r.id);
      if (!board) continue;
      if (!net.sampleRun(r.id, Math.min(9, r.lengthM * 0.06), _pt)) continue;
      const site = plantSign(net, terrain, _pt.x, _pt.z, _pt.tx, _pt.tz, r.treadWidthM);
      if (!site) { this.buildStats.skipped++; continue; }
      const gap = spaced(site.x, site.z);
      if (gap < SIGN_MIN_SPACING) { this.buildStats.skipped++; continue; }
      SIGN_SPACING.observeAt(gap, site.x, site.y, site.z);
      SIGN_CLEARANCE.observeAt(site.clearance, site.x, site.y, site.z);
      SIGN_SLOPE.observeAt(site.slope, site.x, site.y, site.z);
      geo.box(
        site.x, site.y - POST_SINK + (MARKER_H + POST_SINK) * 0.5, site.z,
        POST_W * 0.5, (MARKER_H + POST_SINK) * 0.5, POST_W * 0.5,
        site.yaw, 0, SWATCH_TIMBER, SWATCH_TIMBER,
      );
      geo.box(
        site.x + Math.sin(site.yaw) * (POST_W * 0.5 + BOARD_T * 0.5),
        site.y + MARKER_H - BOARD_H * 0.6,
        site.z + Math.cos(site.yaw) * (POST_W * 0.5 + BOARD_T * 0.5),
        BOARD_W * 0.5, BOARD_H * 0.5, BOARD_T * 0.5,
        site.yaw, 0, board, SWATCH_PLANK,
      );
      this.plates.push({ kind: 'marker', label: r.name, q: Array.from(geo.lastFront) });
      const km = r.lengthM / 1000;
      this.signs.push({
        kind: 'marker', x: site.x, y: site.y, z: site.z, topH: MARKER_H + 0.1,
        label: r.name,
        sub: `${km >= 1 ? `${km.toFixed(2)} KM` : `${r.lengthM.toFixed(0)} M`}`
          + ` · ${r.dropM.toFixed(0)} M DROP`,
        difficulty: r.difficulty, runId: r.id, trailheadId: r.trailheadId,
      });
    }

    // --- 4. a small blaze where a B-line leaves its parent -------------------
    for (const j of net.junctions) {
      if (j.kind !== 'split') continue;
      const bl = j.runIds.map((id) => net.run(id)).find((r) => r && r.isBLine);
      if (!bl) continue;
      const parent = bl.parentRunId ? net.run(bl.parentRunId) : undefined;
      if (!net.sampleRun(bl.id, Math.min(4, bl.lengthM * 0.2), _pt)) continue;
      const site = plantSign(
        net, terrain, j.x, j.z, _pt.tx, _pt.tz, parent?.treadWidthM ?? 1.4,
      );
      if (!site) { this.buildStats.skipped++; continue; }
      const gap = spaced(site.x, site.z);
      if (gap < SIGN_MIN_SPACING) { this.buildStats.skipped++; continue; }
      SIGN_SPACING.observeAt(gap, site.x, site.y, site.z);
      SIGN_CLEARANCE.observeAt(site.clearance, site.x, site.y, site.z);
      SIGN_SLOPE.observeAt(site.slope, site.x, site.y, site.z);
      geo.box(
        site.x, site.y - POST_SINK + (BLINE_H + POST_SINK) * 0.5, site.z,
        POST_W * 0.4, (BLINE_H + POST_SINK) * 0.5, POST_W * 0.4,
        site.yaw, 0, SWATCH_TIMBER, SWATCH_TIMBER,
      );
      geo.box(
        site.x + Math.sin(site.yaw) * (POST_W * 0.4 + 0.012),
        site.y + BLINE_H - BLAZE_H * 0.55,
        site.z + Math.cos(site.yaw) * (POST_W * 0.4 + 0.012),
        BLAZE_W * 0.5, BLAZE_H * 0.5, 0.012,
        site.yaw, 0, blineRect, SWATCH_PLANK,
      );
      this.plates.push({ kind: 'bline', label: 'B-LINE', q: Array.from(geo.lastFront) });
      this.signs.push({
        kind: 'bline', x: site.x, y: site.y, z: site.z, topH: BLINE_H + 0.05,
        label: 'B-LINE', sub: parent ? parent.name.toUpperCase() : '',
        difficulty: '', runId: parent?.id ?? null, trailheadId: null,
      });
    }

    const material = new THREE.MeshStandardMaterial({
      map: tex,
      roughness: 0.88,
      metalness: 0,
      // A blade sign is a flat plate: from behind you see its back, and the back is
      // planking, not a hole. Every face is authored, so single-sided is correct and
      // halves the fragment work.
      side: THREE.FrontSide,
      // The retroreflective gain. See PLATE_GAIN. The map is the atlas itself, so the
      // term is albedo-weighted: a pale plate lifts, its ink type does not, and the
      // timber lifts an eighth as much as the plate does.
      emissiveMap: tex,
      emissive: new THREE.Color(0, 0, 0),
      emissiveIntensity: 1,
    });
    const geometry = geo.build();
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    // One merged mesh spanning the mountain: culling it as a unit would pop the whole
    // set out at once, and its bounding sphere is the world anyway.
    mesh.frustumCulled = false;
    mesh.name = 'content.signage';
    const group = new THREE.Group();
    group.name = 'content';
    group.add(mesh);
    ctx.scene.add(group);
    this.group = group;
    this.mesh = mesh;
    this.signMat = material;

    // The two lights the gain term reads. Found by type rather than by name: the sky
    // module owns them, this module must not, and a missing one is a real state (a probe
    // page with no sky) rather than a crash — the gain simply stays at zero and the
    // signs render exactly as they did before, which is a visible failure, not a silent
    // one.
    ctx.scene.traverse((o) => {
      const l = o as THREE.LightProbe & THREE.DirectionalLight;
      if (l.isLightProbe && !this.skyProbe) this.skyProbe = o as THREE.LightProbe;
      if (l.isDirectionalLight && !this.keyLight) this.keyLight = o as THREE.DirectionalLight;
    });
    this.updatePlateGain();

    this.buildStats.signs = this.signs.length;
    this.buildStats.boxes = geo.boxes;
    this.buildStats.tris = geo.boxes * 12;
    this.buildStats.buildMs = performance.now() - t0;
    console.info(
      `[content] ${this.signs.length} signs planted `
      + `(${this.signs.filter((s) => s.kind === 'kiosk').length} kiosks, `
      + `${this.signs.filter((s) => s.kind === 'marker').length} run markers, `
      + `${this.signs.filter((s) => s.kind === 'finger').length} fingerposts, `
      + `${this.signs.filter((s) => s.kind === 'bline').length} B-line blazes), `
      + `${this.buildStats.skipped} sites rejected, ${geo.boxes} boxes / `
      + `${this.buildStats.tris} tris in 1 draw call, built in `
      + `${this.buildStats.buildMs.toFixed(0)} ms`,
    );
  }

  // -----------------------------------------------------------------------
  // Frame
  // -----------------------------------------------------------------------

  update(dt: number, ctx: SystemContext): void {
    const t0 = performance.now();
    this.frames++;
    const net = this.net;
    if (!net) return;
    const s = ctx.bike;

    this.updatePlateGain();
    this.runMode = false;
    this.trackRun(dt, net, s.x, s.z, ctx);
    // After trackRun (which decides whether there is a run to be on) and before the world
    // labels (which want the nearest-tread point as an anchor when there is not).
    this.updateLocator(dt, ctx);
    this.updateLabels(dt, ctx);
    FRAME_MS.observe(performance.now() - t0);
    // Measured after, and against its own budget, so the piece's per-frame cost and the
    // instrument's cost can never be confused for each other.
    const t1 = performance.now();
    if (this.auditChrome(dt, net, ctx)) AUDIT_MS.observe(performance.now() - t1);
  }

  /**
   * Re-key the plates' retroreflective gain to the light that is actually in the sky.
   *
   * Four multiplies and a `setRGB` on one shared material — no allocation, no traversal,
   * no per-sign work. It runs every frame rather than on a timer because the sky's SH is
   * rebuilt every frame and a plate that lags a sunset by half a second reads as a bug.
   *
   * `sh.coefficients[0]` is the DC term of the sky's SH9 ambient. THREE turns it into
   * irradiance with the band-0 factor 0.886227, and Lambert then divides by PI; folding
   * both in (0.886227 / PI = 0.2821) makes this term the same quantity, in the same
   * units, as the diffuse ambient the plate already receives. So PLATE_GAIN reads
   * directly as "how many times the sky's own ambient this sheeting returns", which is
   * what retroreflective gain means, rather than as an arbitrary brightness.
   */
  private updatePlateGain(): void {
    const m = this.signMat;
    if (!m) return;
    const p = this.skyProbe;
    if (!p) return;
    const c = p.sh.coefficients[0];
    const k = 0.2821 * p.intensity * (plateGainOverride >= 0 ? plateGainOverride : PLATE_GAIN);
    let r = c.x * k;
    let g = c.y * k;
    let b = c.z * k;
    // The warm half of the term. The sun's colour IS its transmittance in this build
    // (`src/render/sky.ts`), so it is already orange at a low sun and already goes to
    // near-black at night — using it as the tint costs nothing and cannot switch the
    // plate on after dark.
    const s = this.keyLight;
    if (s) {
      const lum = r * 0.2126 + g * 0.7152 + b * 0.0722;
      const sc = s.color;
      const sl = sc.r * 0.2126 + sc.g * 0.7152 + sc.b * 0.0722;
      if (sl > 1e-4) {
        // Re-normalised to the same luminance, so PLATE_WARM shifts the HUE only and
        // cannot smuggle in extra brightness behind PLATE_GAIN's back.
        const f = lum / sl;
        r = r * (1 - PLATE_WARM) + sc.r * f * PLATE_WARM;
        g = g * (1 - PLATE_WARM) + sc.g * f * PLATE_WARM;
        b = b * (1 - PLATE_WARM) + sc.b * f * PLATE_WARM;
      }
    }
    m.emissive.setRGB(r, g, b);
    const lift = r * 0.2126 + g * 0.7152 + b * 0.0722;
    PLATE_LIFT.observe(lift);
    // Daylight only: see PLATE_LIFT_DAY. The sun's colour is its own transmittance here,
    // so its luminance is the honest "is the sun up" test and needs nothing from sky.ts.
    if (s) {
      const sl = s.color.r * 0.2126 + s.color.g * 0.7152 + s.color.b * 0.0722;
      if (sl > 0.25) PLATE_LIFT_DAY.observe(lift);
    }
  }

  /** Which run is under the bike, how far down it, and what that run has cost so far. */
  private trackRun(dt: number, net: TrailNetworkApi, x: number, z: number, ctx: SystemContext): void {
    const bike = ctx.bike;
    const p = net.probe(x, z);
    // `weight` is 1 on the compacted centre and 0 at the edge of the disturbed corridor.
    // Requiring the tread itself would drop the readout every time a wheel steps off a
    // berm; requiring only "in the corridor" would keep it while the rider is in the bush.
    const on = p !== null && (p.onTread || p.weight > 0.25);
    let ownerId = '';
    let ownerS = 0;
    let onB = false;
    if (on && p) {
      const r = net.run(p.runId);
      if (r) {
        if (r.isBLine && r.parentRunId) {
          ownerId = r.parentRunId;
          onB = true;
          // A B-line has its own arc length; hold the parent's progress while on it.
          ownerS = this.session && this.session.runId === ownerId ? this.session.maxS : -1;
        } else {
          ownerId = r.id;
          ownerS = p.s;
        }
      }
    }

    // Just finished this one and still standing on it. Not a new descent — but not a
    // reason to go silent either: `updateLocator` puts the strip into `off` mode, which
    // on the tread of the run you have just ridden reads "ON TRAIL · 712 M / SUNDAY BEST
    // / ON THE TREAD". R2 blanked the screen here, which is why the audit's own note said
    // `on Sunday Best, HUD shows ""`.
    if (ownerId && ownerId === this.completedId) return;
    if (this.completedId && ownerId !== this.completedId) {
      this.completedId = '';
      this.completedName = '';
    }

    if (ownerId) {
      this.offT = 0;
      if (!this.session || this.session.runId !== ownerId) {
        // Hysteresis: junctions hand the probe back and forth for a few frames.
        if (this.pendingId === ownerId) this.pendingT += dt; else { this.pendingId = ownerId; this.pendingT = 0; }
        if (this.pendingT >= RUN_SWITCH_S) {
          const info = net.run(ownerId);
          if (info) {
            this.session = newSession(info, ownerS >= 0 ? ownerS : 0);
            this.sessionAge = 0;
            this.pendingId = '';
            this.pendingT = 0;
          }
        }
      } else {
        this.pendingId = '';
        this.pendingT = 0;
      }
    } else {
      this.offT += dt;
      this.pendingId = '';
      this.pendingT = 0;
      if (this.offT > RUN_DROP_S) this.session = null;
    }

    const ses = this.session;
    if (!ses) return;
    this.sessionAge += dt;
    ses.timeS += dt;
    ses.distanceM += bike.speed * dt;
    if (bike.speed > ses.topSpeed) ses.topSpeed = bike.speed;
    if (bike.airborne) ses.airS += dt;
    if (!bike.airborne && ses.wasAirborne && bike.airTime > ses.bestAir) ses.bestAir = bike.airTime;
    ses.wasAirborne = bike.airborne;
    if (bike.crashed && !ses.wasCrashed) { ses.crashes++; ses.featDirty = true; }
    ses.wasCrashed = bike.crashed;

    if (ownerId === ses.runId && ownerS >= 0 && ownerS > ses.maxS) ses.maxS = ownerS;

    // Features: count spans the rider has passed through, and whether they came out of
    // each one still on the bike.
    const feats = ses.info.features;
    while (ses.featIdx < feats.length && ses.maxS > feats[ses.featIdx].s1) {
      ses.featCount++;
      if (!ses.featDirty) ses.featClean++;
      ses.featDirty = false;
      ses.featIdx++;
    }
    if (bike.crashed && ses.featIdx < feats.length && ses.maxS >= feats[ses.featIdx].s0) {
      ses.featDirty = true;
    }

    const info = ses.info;
    this.runMode = true;
    runStrip.name = info.name;
    runStrip.difficulty = info.difficulty;
    runStrip.character = info.character;
    runStrip.lengthM = info.lengthM;
    runStrip.progress = info.lengthM > 0 ? ses.maxS / info.lengthM : 0;
    runStrip.toGoM = Math.max(0, info.lengthM - ses.maxS);
    runStrip.onBLine = onB;

    // Finished?
    const ridden = ses.maxS - ses.startS;
    if (
      ses.maxS >= info.lengthM - RUN_END_TOL
      && ridden >= Math.max(RUN_MIN_METRES, info.lengthM * RUN_MIN_FRACTION)
      && this.sessionAge > 1
    ) {
      this.finish(ses);
    }
  }

  /** Fire the result card for a session and retire it. */
  finish(ses: RunSession): void {
    const prev = bestTimes.get(ses.runId) ?? null;
    const isBest = prev === null || ses.timeS < prev;
    if (isBest) bestTimes.set(ses.runId, ses.timeS);
    const card: RunResultCard = {
      name: ses.info.name,
      difficulty: ses.info.difficulty,
      character: ses.info.character,
      timeS: ses.timeS,
      lengthM: ses.info.lengthM,
      dropM: ses.info.dropM,
      topSpeedKmh: ses.topSpeed * 3.6,
      avgSpeedKmh: ses.timeS > 0 ? (ses.distanceM / ses.timeS) * 3.6 : 0,
      airS: ses.airS,
      bestAirS: ses.bestAir,
      features: ses.featCount,
      cleanFeatures: ses.featClean,
      crashes: ses.crashes,
      bestTimeS: prev,
      isBest,
    };
    showRunResult(card);
    this.session = null;
    this.completedId = ses.runId;
    this.completedName = ses.info.name;
    this.runMode = false;
    runStrip.mode = '';
  }

  // -----------------------------------------------------------------------
  // The locator — the answer when there is no run to be on
  // -----------------------------------------------------------------------

  /**
   * Nearest node of any named centreline to `(x, z)`, refined onto its two segments.
   *
   * A flat scan of 4,860 nodes. No grid, no BVH, no early-out on a bounding box: at 8 Hz
   * this is around 5 us and every acceleration structure is a second source of truth that
   * can be wrong about the mountain. The refinement afterwards is what makes the answer
   * a point on the LINE rather than a point on the polygon's corner — without it the
   * distance readout quantises to the node spacing and the arrow visibly snaps between
   * nodes as the rider moves.
   *
   * Writes `nearRun` / `nearX,Y,Z`. Returns false when nothing is within `LOCATOR_FAR_M`.
   */
  private nearestNamed(x: number, z: number): boolean {
    const lx = this.locX;
    const lz = this.locZ;
    const ly = this.locY;
    const lr = this.locRun;
    if (!lx || !ly || !lz || !lr) return false;
    const n = this.locCount;
    let bestD = Infinity;
    let bi = -1;
    for (let i = 0; i < n; i++) {
      const dx = lx[i] - x;
      const dz = lz[i] - z;
      const d = dx * dx + dz * dz;
      if (d < bestD) { bestD = d; bi = i; }
    }
    if (bi < 0 || bestD > LOCATOR_FAR_M * LOCATOR_FAR_M) { this.nearRun = null; return false; }
    const run = lr[bi];
    // Project onto the segment either side of the winning node, staying inside the same
    // run so a projection never jumps across a junction onto a different line.
    let px = lx[bi];
    let py = ly[bi];
    let pz = lz[bi];
    let pd = bestD;
    for (const j of [bi - 1, bi + 1]) {
      if (j < 0 || j >= n || lr[j] !== run) continue;
      const ax = lx[bi];
      const az = lz[bi];
      const bx = lx[j] - ax;
      const bz = lz[j] - az;
      const len2 = bx * bx + bz * bz;
      if (len2 < 1e-6) continue;
      let t = ((x - ax) * bx + (z - az) * bz) / len2;
      if (t < 0) t = 0; else if (t > 1) t = 1;
      const qx = ax + bx * t;
      const qz = az + bz * t;
      const d = (qx - x) * (qx - x) + (qz - z) * (qz - z);
      if (d < pd) {
        pd = d;
        px = qx;
        pz = qz;
        py = ly[bi] + (ly[j] - ly[bi]) * t;
      }
    }
    this.nearRun = this.namedRuns[run] ?? null;
    this.nearX = px;
    this.nearY = py;
    this.nearZ = pz;
    return this.nearRun !== null;
  }

  /**
   * Drive the strip's `off` mode: state, elevation, the nearest named run, how far its
   * tread is and which way.
   *
   * The bearing is taken in the CAMERA's frame rather than the bike's, deliberately. The
   * arrow is read against the picture, and the picture is the camera's — an arrow that is
   * correct relative to a bike yaw the player cannot see is an arrow that points the wrong
   * way every time the chase camera is still swinging round behind a turn.
   */
  private updateLocator(dt: number, ctx: SystemContext): void {
    if (this.runMode) {
      runStrip.mode = 'run';
      return;
    }
    const b = ctx.bike;
    this.locatorTimer -= dt;
    if (this.locatorTimer <= 0 || !this.nearRun) {
      this.locatorTimer = LOCATOR_REFRESH_S;
      this.nearestNamed(b.x, b.z);
    }
    const r = this.nearRun;
    if (!r) {
      // Genuinely nowhere near the network. Saying nothing is the honest answer, and it is
      // also the state `content.locationOnScreen` is allowed to score 0 on — see there.
      runStrip.mode = '';
      return;
    }
    const dx = this.nearX - b.x;
    const dz = this.nearZ - b.z;
    const dist = Math.hypot(dx, dz);
    const cam = ctx.camera;
    cam.getWorldDirection(_fwd);
    // Signed angle from the camera's forward to the target, about +Y. atan2 of the cross
    // and the dot in XZ, which is stable through 180 degrees where an acos is not.
    const fx = _fwd.x;
    const fz = _fwd.z;
    const fl = Math.hypot(fx, fz) || 1;
    const dl = dist || 1;
    const dot = (fx * dx + fz * dz) / (fl * dl);
    const cross = (fx * dz - fz * dx) / (fl * dl);
    runStrip.mode = 'off';
    runStrip.name = r.name;
    runStrip.difficulty = r.difficulty;
    runStrip.character = r.character;
    runStrip.lengthM = r.lengthM;
    runStrip.distM = dist;
    runStrip.bearing = Math.atan2(cross, dot);
    runStrip.elevM = b.y;
    runStrip.onBLine = false;
  }

  // -----------------------------------------------------------------------
  // World labels
  // -----------------------------------------------------------------------

  /**
   * Is `(x,y,z)` visible from the camera, or is there a hill in the way?
   *
   * Ten samples of the actual heightfield along the ray. This is the difference between a
   * label that names the ridge you are looking at and a label for a run two valleys away
   * that is painted over the hillside in front of it — the same class of mistake as the
   * wave-4 camera metric that counted green pixels with no depth.
   */
  private clearLineOfSight(
    terrain: Terrain, cx: number, cy: number, cz: number, x: number, y: number, z: number,
  ): boolean {
    const steps = 10;
    for (let i = 2; i <= steps - 1; i++) {
      const t = i / steps;
      const sx = cx + (x - cx) * t;
      const sy = cy + (y - cy) * t;
      const sz = cz + (z - cz) * t;
      // 1.2 m of tolerance: the ray grazing a convex ridge should not kill a label that
      // is plainly visible, and no real occluder is under a metre.
      if (terrain.height(sx, sz) > sy + 1.2) return false;
    }
    return true;
  }

  /** Write one candidate into the pool, keeping the best 32 by (priority, distance). */
  private addCandidate(
    x: number, y: number, z: number, label: string, sub: string,
    difficulty: BlazeGrade, priority: number, dist: number, plateH: number,
  ): void {
    const pool = this.candidates;
    let slot = this.candCount;
    if (slot >= pool.length) {
      // Full: displace the worst entry, if this beats it.
      let worst = 0;
      for (let i = 1; i < pool.length; i++) {
        const a = pool[i];
        const b = pool[worst];
        if (a.priority > b.priority || (a.priority === b.priority && a.dist > b.dist)) worst = i;
      }
      const w = pool[worst];
      if (priority > w.priority || (priority === w.priority && dist >= w.dist)) return;
      slot = worst;
    } else {
      this.candCount++;
    }
    const c = pool[slot];
    c.x = x; c.y = y; c.z = z;
    c.label = label; c.sub = sub; c.difficulty = difficulty;
    c.priority = priority; c.dist = dist; c.plateH = plateH;
  }

  private refreshCandidates(ctx: SystemContext): void {
    this.candCount = 0;
    const terrain = this.terrain;
    if (!terrain) return;
    const b = ctx.bike;
    // The orientation window is for a standing start, not for a rider already at 38 km/h:
    // `captures/w5-content-r1-fullrun/fullrun_30p0s.png` had a label up at speed because a
    // new session had just opened. `docs/BAR.md` wants the screen clear while riding.
    // R2 had `|| !runStrip.active` in here, so being off a run put the screen into look
    // mode at any speed. With the way-back chip below that would be four labels up at
    // 60 km/h, and `docs/BAR.md` asks for a clear screen. Off a run at speed you now get
    // exactly one chip and it is the one that says where the trail is.
    const look = this.peek
      || b.speed < POI_SLOW
      || (this.sessionAge < POI_ORIENT_S && b.speed < POI_SLOW * 1.7);
    this.lookMode = look;

    // Decision labels: a junction where the line splits, close enough to act on. These
    // are shown whatever the speed — that is the moment they are for.
    this.atDecision = false;

    /**
     * THE WAY BACK, IN THE WORLD RATHER THAN IN THE CORNER.
     *
     * Off a run, the strip names the nearest line and the arrow says which way — but the
     * arrow is a corner readout and the question "how do I get to it" is a question about
     * the picture. This puts a chip on the actual point of tread the strip is talking
     * about, so the rider steers at a label on the hillside instead of at an arrow.
     *
     * Priority 0, alongside a junction decision: this is a live decision too. `plateH` 0
     * because there is no object here to become legible and stand the chip down — the
     * whole reason the chip is needed is that this stretch of trail has no sign on it.
     * Measured on `vista`: the 3.0 s window at 57-64 km/h where the product said nothing
     * had NO sign anywhere in frame (`captures/w5-content-offrun/OFFRUN.txt` — every
     * marker `behind` or `occluded`), so a chip that depends on signage could not have
     * covered it.
     */
    if (!this.runMode && this.nearRun && runStrip.mode === 'off') {
      const d = Math.hypot(this.nearX - b.x, this.nearZ - b.z);
      if (d > TREAD_NEAR_M) {
        // The label text is rebuilt only when the rounded metre changes, so the steady
        // state of this path allocates nothing — the same rule the candidate pool exists
        // for. `refreshCandidates` runs at ~7 Hz and a template literal here would be 14
        // strings a second for no gain a reader could see.
        const dq = d >= 1000 ? Math.round(d / 10) * 10 : Math.round(d);
        if (dq !== this.wayD || this.wayRunId !== this.nearRun.id) {
          this.wayD = dq;
          this.wayRunId = this.nearRun.id;
          this.waySub = d >= 1000
            ? `${(d / 1000).toFixed(2)} KM · ${this.nearRun.character.toUpperCase()}`
            : `${dq.toFixed(0)} M · ${this.nearRun.character.toUpperCase()}`;
        }
        this.addCandidate(
          this.nearX, this.nearY, this.nearZ,
          this.nearRun.name, this.waySub, this.nearRun.difficulty, 0, d, 0,
        );
      }
    }
    for (const d of this.decisions) {
      const dist = Math.hypot(d.x - b.x, d.z - b.z);
      if (dist > POI_JUNCTION_RANGE) continue;
      this.atDecision = true;
      this.addCandidate(d.x, d.y, d.z, d.a.label, d.a.sub, d.a.grade, 0, dist, BOARD_H);
      this.addCandidate(d.x, d.y, d.z, d.b.label, d.b.sub, d.b.grade, 0, dist + 0.01, BOARD_H);
    }

    if (look) {
      for (const sg of this.signs) {
        if (sg.kind === 'bline' || sg.kind === 'finger') continue;
        const dist = Math.hypot(sg.x - b.x, sg.z - b.z);
        if (dist > POI_FAR || dist < 6) continue;
        // Do not label the run you are already on; the strip already says it.
        if (sg.runId && this.session && sg.runId === this.session.runId) continue;
        this.addCandidate(
          sg.x, sg.y, sg.z, sg.label, sg.sub, sg.difficulty,
          sg.kind === 'kiosk' ? 1 : 2, dist,
          sg.kind === 'kiosk' ? MAP_H : BOARD_H,
        );
      }
    }
    // Insertion sort over the used prefix, swapping references in place. `slice().sort()`
    // would have put the allocation straight back in — n is at most 32 and usually under
    // 6, which is exactly the size insertion sort is for.
    const pool = this.candidates;
    for (let i = 1; i < this.candCount; i++) {
      const v = pool[i];
      let j = i - 1;
      while (j >= 0 && (pool[j].priority > v.priority
        || (pool[j].priority === v.priority && pool[j].dist > v.dist))) {
        pool[j + 1] = pool[j];
        j--;
      }
      pool[j + 1] = v;
    }
  }

  private updateLabels(dt: number, ctx: SystemContext): void {
    for (const l of poiLabels) l.visible = false;
    const terrain = this.terrain;
    if (!terrain) return;
    if (runResultVisible()) { POI_COUNT.observe(0); return; }

    this.candidateTimer -= dt;
    if (this.candidateTimer <= 0) {
      this.candidateTimer = 0.15;
      this.refreshCandidates(ctx);
    }

    const cam = ctx.camera;
    cam.getWorldDirection(_fwd);
    const cx = cam.position.x;
    const cy = cam.position.y;
    const cz = cam.position.z;
    const w = ctx.renderer.domElement.clientWidth || window.innerWidth;
    const h = ctx.renderer.domElement.clientHeight || window.innerHeight;
    let n = 0;
    // At speed on a run you get ONE hint and no more; slow, exploring, or at a junction
    // you get the full set. Same rule as `look` above, applied to the count rather than to
    // the candidate list, so a junction decision still gets both of its branches.
    const cap = (this.lookMode || this.atDecision) ? MAX_POI : 1;
    // Two labels may share a junction anchor, and two run heads a hundred metres apart
    // project to nearly the same pixel. Stack rather than overlap: R1 drew SUMMIT GATE
    // straight through RIPSAW and both became unreadable.
    const takenX = this.takenX;
    const takenY = this.takenY;
    // Pixels of screen height one metre at one metre subtends, so a plate's on-screen
    // height is `plateH * pxPerM / dist`. One tan per frame rather than one per label.
    const pxPerM = (h * 0.5) / Math.tan((cam.fov * Math.PI) / 360);
    for (let ci = 0; ci < this.candCount; ci++) {
      const c = this.candidates[ci];
      if (n >= cap) break;
      // The object can speak for itself: stand the chip down. See POI_SIGN_LEGIBLE_PX.
      if (c.plateH > 0 && (c.plateH * pxPerM) / Math.max(1, c.dist) >= POI_SIGN_LEGIBLE_PX) {
        continue;
      }
      const ax = c.x;
      const ay = c.y + 1.55;
      const az = c.z;
      // In front of the camera at all?
      if ((ax - cx) * _fwd.x + (ay - cy) * _fwd.y + (az - cz) * _fwd.z <= 1) continue;
      if (!this.clearLineOfSight(terrain, cx, cy, cz, ax, ay, az)) continue;
      _v.set(ax, ay, az).project(cam);
      if (_v.z > 1) continue;
      let sx = (_v.x * 0.5 + 0.5) * w;
      let sy = (-_v.y * 0.5 + 0.5) * h;
      // Off the glass, or in the corner the speed block owns.
      if (sx < 70 || sx > w - 70 || sy < 60 || sy > h - 130) continue;
      // Lift until this label clears every one already placed. A chip is ~46 px tall and
      // ~210 px wide; 58 leaves a visible gap between two stacked ones.
      for (let pass = 0; pass < MAX_POI; pass++) {
        let clash = false;
        for (let k = 0; k < n; k++) {
          if (Math.abs(sx - takenX[k]) < 210 && Math.abs(sy - takenY[k]) < 58) {
            sy = takenY[k] - 58;
            clash = true;
          }
        }
        if (!clash) break;
      }
      if (sy < 60) continue;
      takenX[n] = sx;
      takenY[n] = sy;
      const fade = c.dist > POI_FAR - POI_FADE
        ? Math.max(0, (POI_FAR - c.dist) / POI_FADE)
        : 1;
      const l = poiLabels[n];
      l.visible = true;
      l.sx = sx;
      l.sy = sy;
      l.alpha = 0.86 * fade;
      l.label = c.label;
      l.sub = c.sub;
      l.difficulty = c.difficulty;
      n++;
    }
    POI_COUNT.observe(n);
  }

  // -----------------------------------------------------------------------
  // The instrument
  // -----------------------------------------------------------------------

  /**
   * Compare what the trail network says is under the bike with what the HUD is actually
   * painting, read back off the DOM.
   *
   * Sampled at 5 Hz rather than every frame because `hudChromeMetrics()` forces layout.
   * `pctOver` is therefore a percentage of samples, not of frames, and 5 Hz over a
   * 24 s scenario is 120 samples — enough for a percentage to mean something.
   */
  /**
   * The instrument's OWN answer to "what is the nearest named run, and how far".
   *
   * It goes through the trail network's `runsNear()` and the runs' own `centreline`
   * arrays, and it shares not one line with `nearestNamed()` above — no flattened arrays,
   * no cached point, no segment refinement, a different search order and a different
   * escalation. That is the entire value of it. `content.locatorDistErrM` is the
   * disagreement between the two, and a bug in either shows up there rather than in a
   * confident readout that nothing checks.
   *
   * Allocates (`runsNear` builds an array). 2 Hz, charged to `content.auditMs`.
   */
  private auditNearestName(net: TrailNetworkApi, x: number, z: number): {
    name: string; dist: number;
  } {
    let best = '';
    let bestD = Infinity;
    for (const radius of [30, 120, 500, LOCATOR_FAR_M]) {
      for (const r of net.runsNear(x, z, radius)) {
        if (r.isBLine) continue;
        const c = r.centreline;
        for (let i = 0; i < c.length; i += 3) {
          const dx = c[i] - x;
          const dz = c[i + 2] - z;
          const d2 = dx * dx + dz * dz;
          if (d2 < bestD) { bestD = d2; best = r.name; }
        }
      }
      if (best) break;
    }
    return { name: best, dist: best ? Math.sqrt(bestD) : Infinity };
  }

  private auditChrome(dt: number, net: TrailNetworkApi, ctx: SystemContext): boolean {
    this.auditTimer -= dt;
    if (this.auditTimer > 0) return false;
    const period = 0.5;
    this.auditTimer = period;
    const m = hudChromeMetrics();
    const b = ctx.bike;
    const w = ctx.renderer.domElement.clientWidth || window.innerWidth;
    const h = ctx.renderer.domElement.clientHeight || window.innerHeight;

    // --- (a) the defect metric ---------------------------------------------
    // Truth comes from the network, not from `runStrip`.
    const p = net.probe(b.x, b.z);
    let expect = '';
    if (p && (p.onTread || p.weight > 0.25)) {
      const r = net.run(p.runId);
      if (r) {
        const ownerId = r.isBLine && r.parentRunId ? r.parentRunId : r.id;
        expect = net.run(ownerId)?.name ?? '';
      }
    }
    // Settling. The strip deliberately waits RUN_SWITCH_S before it changes run, because
    // a junction hands the probe back and forth for a few frames and a readout that
    // flickers between two names is worse than one that is late. Counting those frames as
    // failures would put a permanent ~2% floor under a metric whose whole value is that 0
    // means 0, so the invariant allows the display its own settling time and no more.
    if (expect === this.auditExpect) this.auditSettle += period;
    else { this.auditExpect = expect; this.auditSettle = 0; }
    const settled = this.auditSettle >= RUN_SWITCH_S + period;

    // A run whose result has just been shown is deliberately not tracked again until the
    // rider leaves it; that is a state, not a miss. R2 returned early here, which also
    // skipped the clutter budget and (had it existed) the headline — the guard belongs on
    // the one invariant it is about.
    const justFinished = !!expect && expect === this.completedName;

    if (expect && settled && !justFinished && !runResultVisible()) {
      const shown = m.stripName.trim().toUpperCase() === expect.trim().toUpperCase();
      RUN_NAMED.observeAt(shown ? 0 : 1, b.x, b.y, b.z);
      if (!shown) RUN_NAMED.noteWorst(`on ${expect}, HUD shows "${m.stripName}"`);
      // Only meaningful while the strip claims to be up: a strip that is correctly
      // absent has no area to measure.
      if (m.stripOpacity > 0.05) STRIP_PX.observeAt(m.stripPx, b.x, b.y, b.z);
    }

    // --- (c) THE HEADLINE: does the screen say where the rider is? ----------
    //
    // Unconditional. There is no `if` in front of this observe and there must never be
    // one: the whole defect R2 shipped was a metric that only sampled in the state it was
    // already known to handle. See LOCATED.
    //
    // The result card is the one deliberate takeover of the screen and it names the run
    // itself in 40 px type, so a sample there would be measuring the wrong surface.
    if (!m.resultOn) {
      const truth = this.auditNearestName(net, b.x, b.z);
      const shownName = m.stripName.trim().toUpperCase();
      // `stripKind` is only painted in `off` mode, so this reads the MODE off the glass.
      const offMode = m.stripKind.trim().length > 0;
      let bad = 0;
      if (!shownName) {
        // Nothing painted. That is only acceptable where there is genuinely nothing to
        // say — off the end of the network, past the locator's own reach.
        bad = Number.isFinite(truth.dist) && truth.dist <= LOCATOR_FAR_M ? 1 : 0;
        if (bad) {
          LOCATED.noteWorst(
            `HUD blank at ${b.x.toFixed(0)},${b.z.toFixed(0)} — ${truth.name} is `
            + `${truth.dist.toFixed(0)} m away`,
          );
        }
      } else if (expect) {
        // On a tread: the name has to be THAT run, not merely a run.
        bad = shownName === expect.trim().toUpperCase() ? 0 : 1;
        if (bad) LOCATED.noteWorst(`on ${expect}, HUD shows "${m.stripName}"`);
      } else {
        // Off any tread: the name has to belong to a line that is really within reach,
        // which is what stops a stale strip from scoring as located.
        bad = truth.name && shownName === truth.name.trim().toUpperCase() ? 0 : 1;
        if (bad) {
          LOCATED.noteWorst(
            `HUD shows "${m.stripName}" but the nearest line is `
            + `${truth.name || 'none'} at ${truth.dist.toFixed(0)} m`,
          );
        }
      }
      LOCATED.observeAt(bad, b.x, b.y, b.z);

      // The displayed metres against the independent search. Only while the locator is
      // the thing on screen — in `run` mode the footer is a distance to go, not a
      // distance to a tread, and comparing them would be comparing two different numbers.
      if (offMode && !bad && Number.isFinite(truth.dist)) {
        LOCATOR_ERR.observeAt(Math.abs(runStrip.distM - truth.dist), b.x, b.y, b.z);
      }
    }

    // --- (b) the clutter budget --------------------------------------------
    if (!m.resultOn) HUD_FRAC.observeAt(m.totalPx / Math.max(1, w * h), b.x, b.y, b.z);
    return true;
  }

  // -----------------------------------------------------------------------
  // Hooks
  // -----------------------------------------------------------------------

  setSignsVisible(v: boolean): void {
    if (this.group) this.group.visible = v;
  }

  signsVisible(): boolean {
    return this.group ? this.group.visible : false;
  }

  setPeek(v: boolean): void {
    this.peek = v;
  }

  signList(): Sign[] {
    return this.signs;
  }

  plateList(): { kind: string; label: string; q: number[] }[] {
    return this.plates;
  }

  currentSession(): RunSession | null {
    return this.session;
  }

  /** What the locator last resolved to. Read by the probes; nothing in the game calls it. */
  locator(): {
    mode: string; name: string; distM: number; bearingDeg: number; elevM: number;
    x: number; y: number; z: number;
  } {
    return {
      mode: runStrip.mode,
      name: this.nearRun ? this.nearRun.name : '',
      distM: runStrip.distM,
      bearingDeg: (runStrip.bearing * 180) / Math.PI,
      elevM: runStrip.elevM,
      x: this.nearX, y: this.nearY, z: this.nearZ,
    };
  }

  /** The instrument's independent nearest-run answer, for the falsification probe. */
  auditNearest(x: number, z: number): { name: string; dist: number } | null {
    return this.net ? this.auditNearestName(this.net, x, z) : null;
  }

  dispose(): void {
    if (!this.group) return;
    this.mesh?.geometry.dispose();
    (this.mesh?.material as THREE.Material | undefined)?.dispose();
    this.texture?.dispose();
    this.group.removeFromParent();
    this.group = null;
  }
}

const content = new Content();

// ---------------------------------------------------------------------------
// Authoring / diagnostic hook. Same shape as the ones sky, trails and post expose.
// `setSignsVisible` is what the A/B pixel probe drives; nothing in the game calls it.
// ---------------------------------------------------------------------------

export interface GripContentHook {
  signs(): { count: number; kinds: Record<string, number>; boxes: number; tris: number };
  anchors(): { kind: string; x: number; y: number; z: number; label: string }[];
  /** The painted face of every plate, as four world corners (bl, br, tr, tl). */
  plates(): { kind: string; label: string; q: number[] }[];
  /** Sweep the retroreflective gain. Negative restores `PLATE_GAIN`. */
  setPlateGain(v: number): void;
  plateGain(): number;
  setSignsVisible(v: boolean): void;
  signsVisible(): boolean;
  strip(): Record<string, unknown>;
  /** What the off-run locator resolved to, and the point it is pointing at. */
  locator(): ReturnType<Content['locator']>;
  /** The audit's own nearest-run search — a second implementation, for cross-checking. */
  auditNearest(x: number, z: number): { name: string; dist: number } | null;
  chrome(): ReturnType<typeof hudChromeMetrics>;
  pois(): { label: string; sub: string; sx: number; sy: number; alpha: number }[];
  peek(v: boolean): void;
  /** Fire the result card from the live session — for capturing it without a 30 min run. */
  finishNow(): boolean;
  dismiss(): void;
}

declare global {
  interface Window {
    __gripContent?: GripContentHook;
  }
}

registerSystem({
  id: 'content',
  order: 40,
  init(ctx: SystemContext) {
    content.init(ctx);
    if (typeof window !== 'undefined') {
      window.__gripContent = {
        signs: () => {
          const kinds: Record<string, number> = {};
          for (const s of content.signList()) kinds[s.kind] = (kinds[s.kind] ?? 0) + 1;
          return {
            count: content.signList().length,
            kinds,
            boxes: content.buildStats.boxes,
            tris: content.buildStats.tris,
          };
        },
        anchors: () => content.signList().map((s) => ({
          kind: s.kind, x: s.x, y: s.y, z: s.z, label: s.label,
        })),
        plates: () => content.plateList(),
        setPlateGain: (v: number) => { plateGainOverride = v; },
        plateGain: () => (plateGainOverride >= 0 ? plateGainOverride : PLATE_GAIN),
        setSignsVisible: (v: boolean) => content.setSignsVisible(v),
        signsVisible: () => content.signsVisible(),
        strip: () => ({ ...runStrip }),
        locator: () => content.locator(),
        auditNearest: (x: number, z: number) => content.auditNearest(x, z),
        chrome: () => hudChromeMetrics(),
        pois: () => poiLabels.filter((p) => p.visible).map((p) => ({
          label: p.label, sub: p.sub, sx: p.sx, sy: p.sy, alpha: p.alpha,
        })),
        peek: (v: boolean) => content.setPeek(v),
        finishNow: () => {
          const s = content.currentSession();
          if (!s) return false;
          content.finish(s);
          return true;
        },
        dismiss: () => hideRunResult(),
      };
    }
    // `M` peeks at the world labels while held — the "what is that over there" key. It
    // is not in controls.ts's KEY_MAP, so it collides with nothing the bike listens for.
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', (e) => { if (e.code === 'KeyM') content.setPeek(true); });
      window.addEventListener('keyup', (e) => { if (e.code === 'KeyM') content.setPeek(false); });
    }
  },
  update(dt: number, ctx: SystemContext) {
    content.update(dt, ctx);
  },
  dispose() {
    content.dispose();
  },
});

export {};
